import { RouterProvider, useLocation, useNavigate } from '../hooks/router';
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore
} from 'react';

import debounce from 'lodash.debounce';

import { calculateGlobalCSS, calculateStepCSS } from '../utils/hydration';
import { FeatheryCacheProvider } from '../utils/emotionCache';
import {
  clearBrowserErrors,
  getAllElements,
  isElementInViewport,
  lookUpTrigger,
  mapFormSettingsResponse,
  prioritizeActions,
  processFileValues,
  registerRenderCallback,
  remountAllForms,
  removeFilePathMapEntry,
  rerenderAllForms,
  setFormElementError,
  updateCustomCSS,
  updateCustomHead
} from '../utils/formHelperFunctions';
import {
  changeStep,
  getInitialStep,
  getNewStepUrl,
  getOrigin,
  getPrevStepKey,
  getUrlHash,
  isStepTerminal,
  nextStepKey,
  recurseProgressDepth,
  setUrlStepHash
} from '../utils/stepHelperFunctions';
import {
  castHiddenVal,
  castServarVal,
  FieldOptions,
  FieldProperties,
  FieldStyles,
  formatStepFields,
  getAllFields,
  FILE_FIELD_TYPES,
  getDefaultFieldValue,
  getDefaultFormFieldValue,
  normalizeRepeatArrayValue,
  stripEmptyRepeatEntries,
  getFieldValue,
  saveInitialValuesAndUrlParams,
  updateStepFieldOptions,
  updateStepFieldProperties,
  updateStepFieldStyles
} from '../utils/fieldHelperFunctions';
import {
  getContainerById,
  getFieldsInRepeat,
  getRepeatedContainer,
  getRepeatErrorOwnerIds
} from '../utils/repeat';
import {
  getHideIfReferences,
  getPositionKey,
  getTextVariableReferences,
  getVisiblePositions
} from '../utils/hideAndRepeats';
import {
  isFieldValueEmpty,
  validateElements,
  validators
} from '../utils/validation';
import {
  defaultClient,
  adoptLinkRedemption,
  FieldValues,
  fieldValues,
  fileRetryStatus,
  initInfo,
  initState,
  updateUserId
} from '../utils/init';
import { isEmptyArray, justInsert, justRemove, toList } from '../utils/array';
import { InlineErrors, shiftInlineErrorRows } from '../utils/inlineErrors';
import FeatheryClient, { API_URL } from '../utils/featheryClient';
import { useFirebaseRecaptcha } from '../integrations/firebase';
import { openPlaidLink } from '../integrations/plaid';
import {
  addToCart,
  checkForPaymentCheckoutCompletion,
  getCart,
  getLiveOrTestProduct,
  getSimplifiedProducts,
  isProductInPurchaseSelections,
  purchaseCart,
  removeFromCart,
  setupPaymentMethod,
  usePayments
} from '../integrations/stripe';
import {
  ActionData,
  filterTrackedFields,
  trackEvent
} from '../integrations/utils';
import DevNavBar from './components/DevNavBar';
import FeatherySpinner from '../elements/components/Spinner';
import CallbackQueue from '../utils/callbackQueue';
import {
  downloadAllFileUrls,
  featheryWindow,
  isIOS,
  openTab,
  runningInClient
} from '../utils/browser';
import FormOff, {
  CLOSED,
  COLLAB_COMPLETED,
  COLLAB_DIRECT_DISABLED,
  FILLED_OUT,
  LINK_REQUIRED,
  NO_BUSINESS_EMAIL
} from '../elements/components/FormOff';
import LinkContinue from '../elements/components/LinkContinue';
import { canPersistLinkSecret } from '../utils/accessLink';
import type { LinkConfirmOutcome, LinkRedemption } from '../utils/accessLink';
import Lottie from '../elements/components/Lottie';
import Watermark from '../elements/components/Watermark';
import Grid from './grid';
import { preloadStepFields } from '../elements/fields';
import { DEFAULT_MOBILE_BREAKPOINT, getViewport } from '../elements/styles';
import {
  ContextOnAction,
  ContextOnChange,
  ContextOnError,
  ContextOnSubmit,
  ContextOnView,
  ElementProps,
  FormContext,
  LogicRule,
  PopupOptions,
  Subgrid,
  Trigger
} from '../types/Form';
import usePrevious from '../hooks/usePrevious';
import ReactPortal from './components/ReactPortal';
import { replaceTextVariables } from '../elements/components/TextNodes';
import { getFormContext } from '../utils/formContext';
import { getPrivateActions } from '../utils/sensitiveActions';
import { v4 as uuidv4 } from 'uuid';
import internalState, {
  CreateEgnyteFolderParams,
  GetDocusignEnvelopeParams,
  UploadFileToEgnyteParams,
  RunComputerAgentOptions,
  SendDocusignParams,
  UpdateDocusignEnvelopeParams,
  setFormInternalState
} from '../utils/internalState';
import {
  ExtractionActionOptions,
  FillQuikParams,
  ForwardInboxEmailOptions,
  PageSelectionInput
} from '@feathery/client-utils';
import useFormAuth from '../auth/internal/useFormAuth';
import {
  ACTION_ADD_REPEATED_ROW,
  ACTION_AI_EXTRACTION,
  ACTION_ALLOY_VERIFY_ID,
  ACTION_BACK,
  ACTION_CONNECT_ACCOUNT,
  ACTION_GENERATE_ENVELOPES,
  ACTION_GENERATE_QUIK_DOCUMENTS,
  ACTION_INVITE_COLLABORATOR,
  ACTION_LOGOUT,
  ACTION_NEW_SUBMISSION,
  ACTION_NEXT,
  ACTION_OAUTH_LOGIN,
  ACTION_START_DATA_MAPPING,
  ACTION_PURCHASE_PRODUCTS,
  ACTION_REMOVE_PRODUCT_FROM_PURCHASE,
  ACTION_REMOVE_REPEATED_ROW,
  ACTION_REWIND_COLLABORATION,
  ACTION_SCHWAB_CREATE_CONTACT,
  ACTION_SELECT_PRODUCT_TO_PURCHASE,
  ACTION_SEND_EMAIL_CODE,
  ACTION_SEND_MAGIC_LINK,
  ACTION_SEND_SMS_CODE,
  ACTION_SEND_SMS_MESSAGE,
  ACTION_SET_TASK_STATUS,
  ACTION_STORE_FIELD,
  ACTION_TELESIGN_PHONE_TYPE,
  ACTION_TELESIGN_SILENT_VERIFICATION,
  ACTION_TELESIGN_SMS_OTP,
  ACTION_TELESIGN_VERIFY_OTP,
  ACTION_TELESIGN_VOICE_OTP,
  ACTION_TRIGGER_ARGYLE,
  ACTION_TRIGGER_FLINKS,
  ACTION_TRIGGER_PERSONA,
  ACTION_TRIGGER_PLAID,
  ACTION_URL,
  ACTION_VERIFY_COLLABORATOR,
  ACTION_VERIFY_EMAIL,
  ACTION_VERIFY_SMS,
  canRunAction,
  hasFlowActions,
  isRunnableStepEventRule,
  REQUIRED_FLOW_ACTIONS
} from '../utils/elementActions';
import { openArgyleLink } from '../integrations/argyle';
import { authState } from '../auth/LoginForm';
import {
  getAuthIntegrationMetadata,
  isTerminalStepAuth
} from '../auth/internal/utils';
import Auth from '../auth/internal/AuthIntegrationInterface';
import { CloseIcon } from '../elements/components/icons';
import useLoader, { InitialLoader } from '../hooks/useLoader';
import { installRecaptcha, verifyRecaptcha } from '../integrations/recaptcha';
import { fieldAllowedFromList } from './grid/Element/utils/utils';
import { triggerPersona } from '../integrations/persona';
import Collaborator from '../utils/entities/Collaborator';
import {
  useOfflineRequestHandler,
  verifyConnectivity
} from '../utils/offlineRequestHandler';
import {
  removeCustomErrorHandler,
  setCustomErrorHandler
} from '../utils/error';
import { verifyAlloyId } from '../integrations/alloy';
import { useFlinksConnect } from '../integrations/flinks';
import ConnectAccountModal from '../integrations/connectAccount/ConnectAccountModal';
import {
  CONFIG_COMPONENTS,
  connectionFieldKey,
  hasEmailIdentity
} from '../integrations/connectAccount/providers';
import {
  ACCOUNT_CONNECT_POPUP_NAME,
  getPopupFeatures,
  runOAuthPopup
} from '../integrations/connectAccount/oauthPopup';
import { isNum } from '../utils/primitives';
import {
  editorContainerId,
  getSignUrl,
  isDocusignSignAction
} from '../utils/document';
import QuikFormViewer from '../elements/components/QuikFormViewer';
import DataMappingModal from '../elements/components/dataMapping/DataMappingModal';
import { createSchwabContact } from '../integrations/schwab';
import { getLoginStep } from '../auth/utils';
import usePollFuserData from '../hooks/usePollFuserData';
import { SharedCodeInfo } from './definitions';
import {
  extractExportedCodeInfoArray,
  handleRuleError,
  runClientSideLogic,
  runServerSideLogic
} from './logic';
import { useCheckButtonAction } from './hooks/useCheckButtonAction';
import ActionToast from './components/ActionToast';
import FileUploadToast from './components/FileUploadToast';
import {
  getUploadToastHeight,
  setUploadIndicatorEnabled,
  subscribeToUploadToastHeight
} from '../utils/fileUploadProgress';
import { useAIExtractionToast } from './components/ActionToast/useAIExtractionToast';
import { useEnvelopeGenerationToast } from './components/ActionToast/useEnvelopeGenerationToast';
import { useTrackUserInteraction } from './hooks/useTrackUserInteraction';
import { AssistantChat } from '../assistant';
import type {
  AssistantLayoutState,
  AssistantStepSettings
} from '../assistant/AssistantChat';
import AssistantClient from '../assistant/AssistantClient';
import {
  getActiveDocxEditorEnvelopeTarget,
  getActiveDocxEditorTarget
} from '../assistant/tools/docx/docxEditorRegistry';
import { confirmLeavingUnsavedWork } from '../utils/unsavedWork';

const DocumentViewer = React.lazy(
  () => import('../elements/components/DocumentViewer')
);

// The action flow keys its toast items by action index; the logic-rule and
// container entry points have no index, so they announce under their own ids.
const ENVELOPE_FLOW_TOAST_ID = 'envelope-flow';
const ENVELOPE_CONTAINER_TOAST_ID = 'envelope-container';

// Shown instead of the closed-form copy when the session request itself fails
// while an access link is open. A token this device kept for the form makes
// that path reachable on an ordinary connectivity failure, where the form is
// not closed at all and a reload is all the user needs.
const LINK_SESSION_FAILED_MESSAGE =
  "We couldn't load this form. Please refresh the page.";

// Bottom-right overlays stack rather than overlap: each one clears the boxes
// already sitting below it, plus a gap. An absent box contributes nothing.
const BOTTOM_RIGHT_STACK_GAP = 10;
const stackAbove = (height: number) =>
  height > 0 ? height + BOTTOM_RIGHT_STACK_GAP : 0;

export * from './grid/StyledContainer';
export type { StyledContainerProps } from './grid/StyledContainer';

export interface Props {
  formId: string;
  onChange?: null | ((context: ContextOnChange) => Promise<any> | void);
  onLoad?: null | ((context: FormContext) => Promise<any> | void);
  onFormLoad?: null | ((context: FormContext) => Promise<any> | void);
  onFormComplete?: null | ((context: FormContext) => Promise<any> | void);
  onSubmit?: null | ((context: ContextOnSubmit) => Promise<any> | void);
  onError?: null | ((context: ContextOnError) => Promise<any> | void);
  onView?: null | ((context: ContextOnView) => Promise<any> | void);
  onAction?: null | ((context: ContextOnAction) => Promise<any> | void);
  onViewElements?: string[];
  saveUrlParams?: boolean;
  initialValues?: FieldValues;
  initialStepId?: string;
  hideTestUI?: boolean;
  language?: string;
  initialLoader?: InitialLoader;
  popupOptions?: PopupOptions;
  elementProps?: ElementProps;
  contextRef?: React.MutableRefObject<null | FormContext>;
  formProps?: Record<string, any>;
  customComponents?: Record<string, any>;
  style?: { [cssProperty: string]: string };
  className?: string;
  children?: ReactNode;
  _draft?: boolean;
  readOnly?: boolean;
  hashNavigation?: boolean;
}

interface InternalProps {
  _internalId: string; // Used to uniquely identify forms when the same form is rendered multiple times
  _isAuthLoading?: boolean; // Flag to show the loader for auth purposes
  _bypassCDN?: boolean; // Fetch form directly from API if true
  _pollFuserData?: boolean; // Poll for updated fuser data on BE. Used by audio AI.
}

export interface ClickActionElement {
  id: string;
  properties: { [key: string]: any };
  repeat?: any;
}

const getSubmissionErrorMessage = (error: unknown): string => {
  if (error instanceof TypeError && error.message === 'Failed to fetch') {
    return 'Unable to upload files. Please check your connection and try again.';
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'Submission failed. Please try again.';
};

function closePreOpenedWindows(windows: Map<number, Window | null>) {
  windows.forEach((win) => win?.close());
}

// Pre-open windows synchronously within the user-gesture call stack.
// Connect Account always needs its popup opened this way since the OAuth
// start call is async; iOS Safari additionally blocks window.open() for any
// action once an await breaks the gesture chain, so tab-opening URL actions
// get the same treatment there.
function preOpenActionWindows(actions: any[]) {
  const windows = new Map<number, Window | null>();
  actions.forEach((action, idx) => {
    if (action.type === ACTION_CONNECT_ACCOUNT) {
      windows.set(
        idx,
        featheryWindow().open(
          'about:blank',
          ACCOUNT_CONNECT_POPUP_NAME,
          getPopupFeatures()
        )
      );
    } else if (isIOS() && action.type === ACTION_URL && action.open_tab) {
      const win = featheryWindow().open('about:blank', '_blank');
      if (win) {
        win.opener = null;
        windows.set(idx, win);
      }
    }
  });
  return windows;
}

function Form({
  _internalId,
  _isAuthLoading = false,
  _bypassCDN = false,
  _draft = false,
  _pollFuserData = false,
  formId: formIdProp, // The 'live' env slug
  onChange = null,
  onLoad = null,
  onFormLoad = null,
  onFormComplete = null,
  onSubmit = null,
  onError = null,
  onView = null,
  onAction = null,
  onViewElements = [],
  saveUrlParams = false,
  hideTestUI = false,
  initialValues = {},
  initialStepId = '',
  language,
  initialLoader,
  popupOptions,
  elementProps = {},
  contextRef,
  formProps = {},
  customComponents = {},
  style = {},
  className = '',
  children,
  readOnly = false,
  hashNavigation
}: InternalProps & Props) {
  const [formName, setFormName] = useState('');
  const [formId, setFormId] = useState(formIdProp);
  const clientRef = useRef<any>(undefined);
  const client = clientRef.current;
  const assistantClientRef = useRef<AssistantClient | undefined>(undefined);
  const navigate = useNavigate();
  const location = useLocation();
  const session = initState.formSessions[formId];

  const [autoValidate, setAutoValidate] = useState(false);

  const [steps, setSteps] = useState<Record<string, any>>({});
  const [activeStep, setActiveStep] = useState<any>(null);
  const [stepKey, setStepKey] = useState('');
  const [shouldScrollToTop, setShouldScrollToTop] = useState(false);
  const pendingScrollRef = useRef<boolean | null>(null);
  const [finished, setFinished] = useState(false);
  const [userProgress, setUserProgress] = useState(null);
  const [curDepth, setCurDepth] = useState(0);
  const [maxDepth, setMaxDepth] = useState(0);
  // No state since off reason is set in two locations almost simultaneously
  const formOffReason = useRef('');
  // Copy for a blocked state the reason alone doesn't describe well enough.
  // Empty leaves FormOff on the standard message for the reason.
  const formOffMessage = useRef('');
  // A single-use access link that this device hasn't claimed yet: the form
  // stays behind the Continue screen until the user redeems it.
  const [linkConfirmRequired, setLinkConfirmRequired] = useState(false);
  const [formSettings, setFormSettings] = useState({
    readOnly,
    errorType: 'html5',
    autocomplete: 'on',
    autofocus: true,
    showBrand: false,
    brandPosition: undefined,
    autoscroll: 'top_of_form',
    rightToLeft: false,
    allowEdits: true,
    saveUrlParams: false,
    saveHideIfFields: false,
    clearHideIfFields: false,
    showFileUploadProgress: true,
    showDocumentProgress: true,
    completionBehavior: '',
    globalStyles: {},
    mobileBreakpoint: DEFAULT_MOBILE_BREAKPOINT,
    assistantEnabled: false,
    assistantColor: '#6b7280',
    assistantWorkflowActions: [],
    assistantStepSettings: {} as AssistantStepSettings
  });
  const trackHashes = useRef(false);
  const curLanguage = useRef<undefined | string>(undefined);

  const [fieldKeys, setFieldKeys] = useState<string[]>([]);
  const [hiddenFields, setHiddenFields] = useState<Record<string, string>>({});

  // Array of 2 elements. First is field whitelist, second is field blacklist
  const [allowLists, setAllowLists] = useState<any[]>([null, null]);

  const [connectorFields, setConnectorFields] = useState<any>();
  const [logicRules, setLogicRules] = useState<LogicRule[]>([]);
  const [sharedCodes, setSharedCodes] = useState<SharedCodeInfo[]>([]);
  // Per-extraction (and per-variant) file-upload field keys this form should
  // auto-submit before running the extraction, provided by the backend in the
  // panel config. Shape: { [extractionId]: { '' (default) | variantId: keys } }
  const extractionFileFields = useRef<Record<string, Record<string, string[]>>>(
    {}
  );
  const [inlineErrors, setInlineErrors] = useState<InlineErrors>({});
  const [, setRepeatChanged] = useState(false);

  const [integrations, setIntegrations] = useState<null | Record<string, any>>(
    null
  );
  const flowCompleted = useRef(false);
  const [requiredStepAction, setRequiredStepAction] = useState<
    keyof typeof REQUIRED_FLOW_ACTIONS | ''
  >('');
  const formLoadRan = useRef(false);

  // Lookup utility to find a servar (server field definition) by its key.
  // Needed because servars are nested within steps and may be across multiple steps,
  // so we need a way to quickly fetch metadata about a field when tracking retry status.
  const getServarByFieldKey = (fieldKey: string) => {
    for (const step of Object.values(steps)) {
      if (!step?.servar_fields) continue;
      const match = step.servar_fields.find(
        ({ servar }: any) => servar.key === fieldKey
      );
      if (match) return match.servar;
    }
    return null;
  };

  // Like getServarByFieldKey, but also returns the step the field lives on so
  // the file can be submitted with the correct step key.
  const getServarAndStepByFieldKey = (fieldKey: string) => {
    for (const step of Object.values(steps) as any[]) {
      if (!step?.servar_fields) continue;
      const match = step.servar_fields.find(
        ({ servar }: any) => servar.key === fieldKey
      );
      if (match) return { servar: match.servar, step };
    }
    return null;
  };

  // Force-submit the file-upload field(s) an AI extraction reads before it
  // runs, regardless of the triggering button's "Validate & Submit Step"
  // toggle. The backend tells us (per extraction + variant) which of this
  // form's file fields feed the extraction; we submit any that currently have
  // a value. Already-submitted files are safe no-ops (deduped client- and
  // server-side). Only the extraction triggered here submits — downstream
  // chained extractions run server-side and inherit its files.
  const submitExtractionFiles = async (
    extractionId: string,
    variantId = ''
  ) => {
    const entry = extractionFileFields.current[extractionId];
    if (!entry) return;
    // A variant overrides the extraction's file sources, so an absent variant
    // entry means it reads no file field present on this form.
    const fieldKeys = variantId ? entry[variantId] ?? [] : entry[''] ?? [];
    if (!fieldKeys.length) return;

    const fileEntries: { servar: any; stepKey: string }[] = [];
    for (const fieldKey of fieldKeys) {
      const found = getServarAndStepByFieldKey(fieldKey);
      if (!found) continue;
      const { servar, step } = found;
      if (!FILE_FIELD_TYPES.includes(servar.type)) continue;
      if (isFieldValueEmpty(fieldValues[fieldKey], servar)) continue;
      fileEntries.push({
        servar: {
          key: servar.key,
          [servar.type]: fieldValues[fieldKey],
          repeated: Boolean(servar.repeated)
        },
        stepKey: step.key
      });
    }
    if (fileEntries.length) await client.submitFiles(fileEntries);
  };

  // Collects file/signature fields that are still waiting to upload.
  // Status is tracked in fileRetryStatus: false means "in progress/failed", true/null means "completed".
  // This is used to block form completion/submission until all file uploads are confirmed successful,
  // preventing data loss when users have spotty connections or large files.
  const getPendingFileUploadKeys = () => {
    return Object.entries(fileRetryStatus).reduce<string[]>(
      (pending, [fieldKey, status]) => {
        if (status) return pending;
        const servar = getServarByFieldKey(fieldKey);
        if (!servar) return pending;
        if (!FILE_FIELD_TYPES.includes(servar.type)) return pending;
        if (isFieldValueEmpty(fieldValues[fieldKey], servar)) return pending;
        pending.push(fieldKey);
        return pending;
      },
      []
    );
  };

  // Enforces that all file uploads must complete before form submission.
  // Throws if any file uploads are still in progress, preventing incomplete submissions
  // that could cause data integrity issues or orphaned files in the backend.
  const requireSuccessfulFileUploads = () => {
    const pendingKeys = getPendingFileUploadKeys();
    if (!pendingKeys.length) return;
    const plural = pendingKeys.length > 1;
    throw new Error(
      plural
        ? 'Some file uploads are still pending. Please check your connection and try again.'
        : 'A file upload is still pending. Please check your connection and try again.'
    );
  };

  const [viewport, setViewport] = useState(() =>
    getViewport(formSettings.mobileBreakpoint)
  );

  const prevAuthId = usePrevious(authState.authId);
  const prevStepKey = usePrevious(stepKey);

  // Set to trigger conditional renders on field value updates, no need to use the value itself
  const [render, setRender] = useState({ v: 1 });

  const [showQuikFormViewer, setShowQuikFormViewer] = useState(false);
  const [quikHTMLPayload, setQuikHTMLPayload] = useState('');
  const [dataMappingState, setDataMappingState] = useState<{
    show: boolean;
    hubs: any[];
  }>({ show: false, hubs: [] });
  const [reviewViewerPayload, setReviewViewerPayload] = useState<any>(null);
  type ConnectAccountModalState = {
    provider: string;
    // Captured from the triggering runElementActions call: advances the
    // action chain past this action, and ends the button/action's loading
    // state. Each is a closure local to that call, not reachable from here
    // any other way.
    onFlowSuccess: () => Promise<void>;
    onAsyncEnd: () => void;
  };
  const [connectAccountModal, setConnectAccountModal] =
    useState<ConnectAccountModalState | null>(null);
  // React state updates aren't visible inside an already-running async
  // closure until it re-reads them, so an in-flight connect_account branch
  // can't rely on the connectAccountModal state to notice another trigger
  // opened a modal moments ago. This ref is synced alongside that state and
  // is what the guard against concurrent triggers reads.
  const connectAccountModalRef = useRef<ConnectAccountModalState | null>(null);
  const openConnectAccountModal = (value: ConnectAccountModalState) => {
    connectAccountModalRef.current = value;
    setConnectAccountModal(value);
  };
  const closeConnectAccountModal = () => {
    connectAccountModalRef.current = null;
    setConnectAccountModal(null);
  };
  const { openFlinksConnect, flinksFrame } = useFlinksConnect();

  // When the active step changes, recalculate the dimensions of the new step
  const stepCSS = useMemo(() => calculateStepCSS(activeStep), [activeStep]);

  const [assistantLayout, setAssistantLayout] = useState<AssistantLayoutState>({
    mode: 'current',
    isOpen: false,
    side: null,
    width: 0,
    isResizing: false
  });
  const handleAssistantLayoutChange = useCallback(
    (state: AssistantLayoutState) => setAssistantLayout(state),
    []
  );
  const assistantOffsetCSS = useMemo(() => {
    const { side, width, isResizing } = assistantLayout;
    const root = activeStep?.subgrids?.find(
      (g: any) => g.position?.length === 0
    );
    const isFillWidth = root?.width === 'fill' || root?.mobile_width === 'fill';
    const transition = isResizing
      ? 'none'
      : 'max-width 0.25s ease, min-width 0.25s ease, margin 0.25s ease';
    const left = side === 'left' ? width : 0;
    const right = side === 'right' ? width : 0;
    if (isFillWidth) {
      const calc = `calc(100% - ${left + right}px)`;
      const sidebarOpen = !!side && width > 0;
      return {
        minWidth: calc,
        maxWidth: calc,
        marginLeft: `${left}px`,
        marginRight: `${right}px`,
        transition,
        ...(sidebarOpen ? { height: '100vh', overflowY: 'auto' } : {})
      };
    }
    if (!side || !width) return {};
    return {
      marginLeft: side === 'left' ? `${width}px` : 'auto',
      marginRight: side === 'right' ? `${width}px` : 'auto'
    };
  }, [assistantLayout, activeStep]);
  const globalCSS = useMemo(
    () => calculateGlobalCSS(formSettings.globalStyles),
    [formSettings.globalStyles]
  );

  useFirebaseRecaptcha(activeStep);
  const getNextAuthStep = useFormAuth({
    initialStep: getInitialStep({ initialStepId, steps }),
    integrations,
    setStepKey,
    steps,
    client,
    _internalId
  });

  const [backNavMap, setBackNavMap] = useState<Record<string, string>>({});
  const updateBackNavMap = (newNavs: Record<string, string>) =>
    newNavs && setBackNavMap({ ...backNavMap, ...newNavs });

  const formRef = useRef<any>(null);

  const { clearLoaders, stepLoader, buttonLoaders, setLoaders } = useLoader({
    initialLoader,
    _isAuthLoading,
    loaderBackgroundColor: stepCSS?.backgroundColor,
    formRef
  });

  // Completion loader helpers: keep the temporary completion loader scoped, reuse
  // designer-provided button loaders when present, and clear only our entry.
  const COMPLETION_LOADER_KEY = 'completionLoader';
  const completionLoaderButtonIdRef = useRef<string | null>(null);
  const showCompletionLoader = useCallback(
    (button?: ClickActionElement) => {
      setLoaders((loaders: Record<string, any>) => {
        if (button?.properties?.show_loading_icon === 'none') return loaders;
        if (button?.id) {
          completionLoaderButtonIdRef.current = button.id;
          // Ensure the custom loader from designer is encapsulated inside the submit step button
          if (loaders[button.id]) return loaders;
          return {
            ...loaders,
            [button.id]: {
              showOn: 'on_button',
              loader: <FeatherySpinner />,
              type: 'default',
              repeat: button.repeat
            }
          };
        }
        if (loaders[COMPLETION_LOADER_KEY]) return loaders;
        return {
          ...loaders,
          [COMPLETION_LOADER_KEY]: {
            showOn: 'full_page',
            loader: <FeatherySpinner />,
            type: 'default',
            isCompletionLoader: true
          }
        };
      });
    },
    [setLoaders]
  );
  const clearCompletionLoader = useCallback(() => {
    setLoaders((loaders: Record<string, any>) => {
      const nextLoaders = { ...loaders };
      if (completionLoaderButtonIdRef.current) {
        delete nextLoaders[completionLoaderButtonIdRef.current];
        completionLoaderButtonIdRef.current = null;
      }
      delete nextLoaders[COMPLETION_LOADER_KEY];
      return nextLoaders;
    });
  }, [setLoaders]);

  const {
    currentActionExtractions,
    initializeActionExtractions,
    updateExtractionInAction,
    handleExtractionStatusUpdate
  } = useAIExtractionToast();

  const {
    currentEnvelopeGeneration,
    initializeEnvelopeGeneration,
    updateEnvelopeGeneration,
    showEnvelopeOutcome
  } = useEnvelopeGenerationToast();

  // Track ActionToast height for positioning WorkflowChat above it
  const [actionToastHeight, setActionToastHeight] = useState(0);
  const actionToastRef = useRef<HTMLDivElement | null>(null);
  const actionToastObserverRef = useRef<ResizeObserver | null>(null);

  // Reset height when toast data becomes empty
  const actionToastData = useMemo(
    () =>
      formSettings.showDocumentProgress
        ? [...currentActionExtractions, ...currentEnvelopeGeneration]
        : [],
    [
      currentActionExtractions,
      currentEnvelopeGeneration,
      formSettings.showDocumentProgress
    ]
  );

  useEffect(() => {
    if (actionToastData.length === 0) {
      setActionToastHeight(0);
    }
  }, [actionToastData.length]);

  const setActionToastRef = useCallback((node: HTMLDivElement | null) => {
    if (actionToastObserverRef.current) {
      actionToastObserverRef.current.disconnect();
      actionToastObserverRef.current = null;
    }

    actionToastRef.current = node;

    if (node) {
      const observer = new ResizeObserver((entries) => {
        setActionToastHeight(entries[0].contentRect.height);
      });
      observer.observe(node);
      actionToastObserverRef.current = observer;
    }
  }, []);

  // The file upload box is page-level and may be rendered by another form
  // instance, so its height comes from the shared tracker rather than a ref
  const fileUploadToastHeight = useSyncExternalStore(
    subscribeToUploadToastHeight,
    getUploadToastHeight,
    getUploadToastHeight
  );

  // The Feathery badge sits in the bottom-right corner, so overlays there start
  // above it
  const bottomRightBase =
    formSettings.showBrand && formSettings.brandPosition === 'bottom_right'
      ? 67
      : 20;

  // Tracks element to focus
  const focusRef = useRef<any>(undefined);
  // Tracks the execution of user-provided callback functions
  const callbackRef = useRef<any>(new CallbackQueue(null, setLoaders));
  // Tracks if the form has redirected
  const hasRedirected = useRef<boolean>(false);
  const elementClicks = useRef<any>({}).current;

  const extractedSharedCodeInfo = useMemo(() => {
    if (sharedCodes.length < 1) {
      return [];
    }

    return extractExportedCodeInfoArray(
      Object.values(sharedCodes) as SharedCodeInfo[]
    );
  }, [sharedCodes]);

  // All mount and unmount logic should live here
  useEffect(() => {
    registerRenderCallback(_internalId, 'form', () => {
      setRender((render) => ({ ...render }));
    });
    if (
      contextRef &&
      Object.prototype.hasOwnProperty.call(contextRef, 'current')
    )
      contextRef.current = getFormContext(_internalId);

    return () => {
      delete initState.renderCallbacks[_internalId];
      delete initState.redirectCallbacks[_internalId];
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setViewport(getViewport(formSettings.mobileBreakpoint));
    };
    featheryWindow().addEventListener('resize', handleResize);
    return () => featheryWindow().removeEventListener('resize', handleResize);
  }, [formSettings]);

  useTrackUserInteraction(formRef, activeStep, stepKey, formName);

  useEffect(() => {
    const oldLanguage = curLanguage.current;
    curLanguage.current = language;

    if (oldLanguage && oldLanguage !== language) {
      // if language changes, need to remount form to refetch data
      initState.remountCallbacks[_internalId]();
    }
  }, [language]);

  useEffect(() => {
    if (!clientRef.current) return;
    clientRef.current.offlineRequestHandler
      .clearFailedFileUploadRequests()
      .catch(() => {});
  }, []);

  // Logic to run every time step changes
  useEffect(() => {
    if (!activeStep) return;

    setAutoValidate(false); // Each step to initially not auto validate

    // Don't autofocus if it will scroll page
    if (
      formSettings.autofocus &&
      focusRef.current?.focus &&
      isElementInViewport(focusRef.current)
    ) {
      focusRef.current.focus({ preventScroll: true });
      focusRef.current = 'already focused';
    }

    let requiredStepAction: any = '';
    activeStep.buttons.forEach((b: any) =>
      (b.properties.actions ?? []).forEach((action: any) => {
        if (action.type in REQUIRED_FLOW_ACTIONS) {
          requiredStepAction = action.type;
        }
      })
    );
    setRequiredStepAction(requiredStepAction);
  }, [activeStep?.id]);

  // viewElements state
  const [viewElements, setViewElements] = useState<string[]>([]);
  useEffect(() => {
    setViewElements(onViewElements);
  }, [onViewElements.length]);

  // Figure out which fields are used in hide rules so that observed changes can be used
  // to trigger rerenders
  const hideIfFieldReferences = useMemo(() => {
    if (activeStep) return getHideIfReferences(getAllElements(activeStep));
    return new Set<string>();
  }, [activeStep?.id]);

  // Same idea for fields interpolated as {{key}} into text, buttons, tooltips,
  // etc. Without this, typing only rerenders on the empty -> non-empty
  // transition, so the interpolated value appears to lag by a whole value.
  const textVariableFieldReferences = useMemo(() => {
    if (activeStep)
      return getTextVariableReferences(getAllElements(activeStep));
    return new Set<string>();
  }, [activeStep?.id]);

  // Servar per field key. updateFieldValues runs on every keystroke, so this
  // avoids both the linear scan in getServarByFieldKey and the Field entity,
  // whose type getter warns when the field is not on the active form. The whole
  // servar is stored because callers need `repeated` as well as `type`.
  const servarByKey = useMemo(() => {
    const servars = new Map<string, any>();
    Object.values(steps).forEach((step: any) =>
      (step.servar_fields ?? []).forEach((field: any) =>
        servars.set(field.servar.key, field.servar)
      )
    );
    return servars;
  }, [steps]);

  useEffect(() => {
    const autoscroll = formSettings.autoscroll;
    if (!shouldScrollToTop || autoscroll === 'none') return;

    const win = featheryWindow();

    const scroll =
      autoscroll === 'top_of_form'
        ? () => formRef.current?.scrollIntoView({ behavior: 'smooth' })
        : () => win.scrollTo({ top: 0, behavior: 'smooth' });
    win.requestAnimationFrame(scroll);
  }, [stepKey, shouldScrollToTop, formSettings.autoscroll]);

  function updateRepeatValues(
    repeatContainer: Subgrid | undefined,
    getNewVal: any
  ) {
    if (!repeatContainer) return;

    // Collect a list of all relevant repeated elements.
    const repeatedServarFields = getFieldsInRepeat(activeStep, repeatContainer);

    // Update the values by appending a default value for each field
    const updatedValues: Record<string, any> = {};
    repeatedServarFields.forEach((field: any) => {
      updatedValues[field.servar.key] = getNewVal(field);
    });

    setRepeatChanged((repeatChanged) => !repeatChanged);
    // Adding/removing a repeat row is a structural change, not user input on a
    // field. Don't auto-validate here: a brand-new, untouched row must not show
    // a required error (inline or browser-native) until the user actually
    // submits. Submit still validates every row via its own validateElements
    // call, so validation is not weakened.
    // clearErrors stays false too: it would wipe custom validity from EVERY
    // control, so an already-invalid surviving row would silently look valid
    // until the next validation pass. On the add path no rows move, so
    // existing validity stays correct; removal shifts rows across DOM nodes,
    // which removeRepeatedRow handles itself (html5 clear + inline reindex).
    updateFieldValues(updatedValues, {
      triggerErrors: false,
      clearErrors: false
    });
  }

  function addRepeatedRow(repeatContainer: Subgrid | undefined, limit = null) {
    const getNewVal = (field: any) => {
      const val = fieldValues[field.servar.key];
      if (limit && val && Array.isArray(val) && val.length >= limit) return val;
      return [
        // @ts-expect-error TS(2461): Type 'FeatheryFieldTypes' is not an array type.
        ...val,
        getDefaultFieldValue(field)
      ];
    };
    updateRepeatValues(repeatContainer, getNewVal);
  }

  function removeRepeatedRow(
    element: any,
    repeatContainer: Subgrid | undefined
  ) {
    const index = element.repeat;
    if (isNaN(index) && !repeatContainer) return;

    const insideContainer = getRepeatedContainer(activeStep, element);
    const isInsideContainer = Boolean(insideContainer);
    const curRepeatContainer = insideContainer || repeatContainer;

    const removeServars: Record<string, null> = {};
    // The removed row belongs to the container, not to any one field. Taking
    // each field's own length lets a shorter array drop a different row, and a
    // file field is shorter than its siblings whenever it ends in empty rows.
    const fieldsInContainer = curRepeatContainer
      ? getFieldsInRepeat(activeStep, curRepeatContainer)
      : [];
    const containerRows = Math.max(
      0,
      ...fieldsInContainer.map((field: any) => {
        const vals = fieldValues[field.servar.key];
        return Array.isArray(vals) ? vals.length : 0;
      })
    );
    const curIndex = isInsideContainer ? index : containerRows - 1;
    if (curIndex < 0) return;

    const getNewVal = (field: any) => {
      const vals = fieldValues[field.servar.key] as any[];

      removeServars[field.servar.key] = null;

      // filePathMap is indexed by repeat row, so it has to lose the same slot
      // or the surviving files resolve to the removed row's uploaded path.
      if (FILE_FIELD_TYPES.includes(field.servar.type))
        removeFilePathMapEntry(field.servar.key, curIndex);

      const newRepeatedValues = justRemove(vals, curIndex);
      const defaultValue = [getDefaultFieldValue(field)];
      return newRepeatedValues.length === 0 ? defaultValue : newRepeatedValues;
    };
    updateRepeatValues(curRepeatContainer, getNewVal);
    internalState[_internalId].updateFieldOptions(removeServars, curIndex);

    // HTML5-mode errors live in the DOM as setCustomValidity state, and repeat
    // rows are keyed by array position, so removal shifts surviving rows into
    // DOM nodes that keep the previous occupant's validity -- the message would
    // render on the wrong row. DOM validity can't be reindexed, so clear it
    // all; the next validation pass restores any real errors.
    if (formSettings.errorType === 'html5') clearBrowserErrors(formRef);

    // Inline errors for a repeated element live in its `byIndex` map. Drop the
    // removed row's entry and shift higher-indexed rows down so each remaining
    // row keeps its own error instead of inheriting a neighbor's. Operating on
    // `byIndex` (not string keys) means a literal field like `foo-0` is never
    // mistaken for a row of `foo`.
    // Every element in the container can own a per-row error, not just servar
    // fields: buttons (and containers) store submit/action failures under their
    // element id, so they must be shifted too.
    const applyShift = () =>
      setInlineErrors((prev) =>
        shiftInlineErrorRows(
          prev,
          [
            ...Object.keys(removeServars),
            ...getRepeatErrorOwnerIds(activeStep, curRepeatContainer)
          ],
          curIndex
        )
      );
    // A pending async button-error publish (see setButtonError) still carries
    // the pre-removal row index. Shift only after it lands so its error gets
    // reindexed with the surviving rows (or dropped with the removed one)
    // instead of attaching to whichever row now occupies the stale index.
    const pendingPublish =
      internalState[_internalId]?.pendingInlineErrorPublish;
    if (pendingPublish) pendingPublish.then(applyShift);
    else applyShift();
  }

  // Debouncing the validateElements call to rate limit calls
  const debouncedValidate = useCallback(
    debounce((setInlineErrors: any) => {
      // default form validation
      visiblePositions &&
        validateElements({
          step: activeStep,
          visiblePositions,
          triggerErrors: true,
          errorType: formSettings.errorType,
          formRef,
          setInlineErrors
        });
    }, 750),
    [activeStep?.id, formRef]
  );

  // Debouncing the rerender due to changing values of referenced fields in hide if rules.
  // Need to rate limit re-renders here for performance reasons.
  const debouncedRerender = useCallback(
    debounce(() => setRender((render) => ({ ...render })), 500),
    [setRender]
  );

  // Text variables render into visible copy, so this repaint has to feel
  // immediate rather than share the 500ms hideIf budget. A hideIf change can
  // add/remove elements and recompute visible positions; interpolation only
  // re-runs over an already-mounted tree. Leading edge paints the first
  // keystroke, maxWait bounds a fast typist.
  const debouncedTextVariableRerender = useCallback(
    debounce(() => setRender((render) => ({ ...render })), 100, {
      leading: true,
      maxWait: 100
    }),
    [setRender]
  );

  const getAssistantTargets = useCallback(() => {
    const targets: { type: string; id: string }[] = [];
    if (formId) targets.push({ type: 'panel', id: formId });
    if (initState.userId) targets.push({ type: 'fuser', id: initState.userId });
    // The mounted editor is the only source of truth. Generation commonly
    // happens on a previous step, so scanning the current step's actions loses
    // the document exactly when the editor is open. This is still only the
    // document template target; the editor separately publishes its real
    // envelope as the only per-submission index scope.
    const documentTarget = getActiveDocxEditorTarget(_internalId);
    if (documentTarget) targets.push(documentTarget);
    const envelopeTarget = getActiveDocxEditorEnvelopeTarget(_internalId);
    if (envelopeTarget) targets.push(envelopeTarget);
    return targets;
  }, [formId]);

  useEffect(() => {
    return () => {
      debouncedValidate.cancel();
    };
  }, [debouncedValidate]);
  useEffect(() => {
    return () => {
      debouncedRerender.cancel();
    };
  }, [debouncedRerender]);
  useEffect(() => {
    return () => {
      debouncedTextVariableRerender.cancel();
    };
  }, [debouncedTextVariableRerender]);

  // Central place to update field values, with smart rerenders and error management.
  // Normalizes null values in arrays to empty strings (prevents null from appearing in repeated fields).
  // Intelligently rerenders based on what changed: immediate render on empty state transitions,
  // debounced render for hideIf dependencies (perf), and validation if auto-validate is enabled.
  const updateFieldValues = (
    newFieldValues: any,
    { rerender = true, clearErrors = true, triggerErrors = true } = {}
  ) => {
    if (clearErrors) clearBrowserErrors(formRef);
    const entries = Object.entries(newFieldValues);
    if (entries.every(([key, val]) => fieldValues[key] === val)) return false;

    const empty = entries.some(([key, val]) => !val || !fieldValues[key]);
    const hideIfDependenciesChanged = entries.some(
      ([key, val]) => fieldValues[key] !== val && hideIfFieldReferences.has(key)
    );
    const textVariableDependenciesChanged = entries.some(
      ([key, val]) =>
        fieldValues[key] !== val && textVariableFieldReferences.has(key)
    );

    const fields = internalState[_internalId]?.fields;

    // Normalize null to empty string in repeated field arrays.
    // This ensures repeated fields don't display "null" values when items are cleared.
    const transformedFieldValues = Object.entries(newFieldValues).reduce(
      (acc, [key, value]) => {
        const field = fields?.[key];
        if (Array.isArray(value) && field && !field.isHiddenField) {
          acc[key] = normalizeRepeatArrayValue(value, servarByKey.get(key));
        } else {
          acc[key] = value;
        }
        return acc;
      },
      {} as Record<string, any>
    );

    Object.assign(fieldValues, transformedFieldValues);

    // Always rerender from empty state for display purposes
    // If any fields involved in a hideIf have changed, then rerender if
    // its dependencies have changed. The field that changed needs to immediately
    // rerender if specified, but hideIf rerenders can be debounced
    if (rerender || empty) setRender((render) => ({ ...render }));
    else {
      // Not exclusive: a key can drive both a hideIf rule and a text variable,
      // and the text variable needs the faster repaint either way.
      if (hideIfDependenciesChanged) debouncedRerender();
      if (textVariableDependenciesChanged) debouncedTextVariableRerender();
    }

    // Only validate on each field change if auto validate is enabled due to prev submit attempt
    if (autoValidate && triggerErrors) debouncedValidate(setInlineErrors);

    return true;
  };

  // For audio AI only right now
  const [pollFuserData, setPollFuserData] = useState(_pollFuserData);
  // An access link leaves the user id empty until the session (or a redeem)
  // resolves it, and polling without one would fetch a different submission
  usePollFuserData(
    pollFuserData && !!initState.userId,
    client,
    updateFieldValues
  );

  const eventCallbackMap: Record<string, any> = {
    change: onChange,
    load: onLoad,
    form_load: onFormLoad,
    form_complete: onFormComplete,
    submit: onSubmit,
    error: onError,
    view: onView,
    action: onAction
  };

  const eventHasUserLogic = (event: string) => {
    return (
      typeof eventCallbackMap[event] === 'function' ||
      (logicRules &&
        logicRules.some(
          (logicRule: LogicRule) => logicRule.trigger_event === event
        ))
    );
  };

  const handleFormComplete = async () => {
    // Send form completion message for webkit
    if (featheryWindow().webkit?.messageHandlers?.feathery?.postMessage) {
      if (initState.isTestEnv) console.log('Webkit event sent');
      featheryWindow().webkit.messageHandlers.feathery.postMessage(
        'Form completed'
      );
    }
    // Send form completion message for React Native Webview
    if (featheryWindow().ReactNativeWebView) {
      if (initState.isTestEnv) console.log('React Native Webview event sent');
      featheryWindow().ReactNativeWebView.postMessage('Form completed');
    }
    // Send form completion message for Android Webview
    if (global.FeatheryInterface) {
      if (initState.isTestEnv) console.log('Android Webview event sent');
      global.FeatheryInterface.onComplete();
    }

    const data = { ...fieldValues };
    const fieldData: Record<string, any> = {};
    if (integrations?.segment?.metadata.track_fields)
      fieldData.segment = filterTrackedFields(
        data,
        integrations.segment.metadata.track_field_keys
      );
    if (integrations?.amplitude?.metadata.track_fields)
      fieldData.amplitude = filterTrackedFields(
        data,
        integrations.amplitude.metadata.track_field_keys
      );
    if (integrations?.['google-tag-manager']?.metadata.track_fields)
      fieldData['google-tag-manager'] = filterTrackedFields(
        data,
        integrations['google-tag-manager'].metadata.track_field_keys
      );
    trackEvent(integrations, 'FeatheryFormComplete', '', formName, fieldData);

    await runUserLogic('form_complete');
  };

  // Executes user callbacks and backend logic rules for a given event (change, load, submit, etc).
  // Supports both synchronous callbacks and server/client-side logic rules.
  // toAwait parameter allows delaying logic execution until async work completes (e.g., file uploads).
  // 'beforeSubmit' flag prevents double-execution on submit (initial validation vs final submission).
  // Returns true if any logic ran, false if all skipped (useful for conditional behavior).
  const runUserLogic = async (
    event: string,
    getProps: () => Record<string, any> = () => ({}),
    // Container id for container clicks; tab group link_id for tab clicks
    altMatchId?: string | undefined,
    toAwait?: Promise<any>,
    flush = true
  ) => {
    const props = {
      ...getFormContext(_internalId),
      ...getPrivateActions(_internalId),
      ...getProps()
    };

    let logicRan = false;
    if (typeof eventCallbackMap[event] === 'function') {
      // Prevent double-execution of submit callback: only run on first pass (beforeSubmit)
      // or if explicitly requested. This allows logic to run twice if needed (before/after submit).
      // @ts-expect-error
      if (event !== 'submit' || props.beforeSubmit) {
        logicRan = true;
        await eventCallbackMap[event](props);
      }
    }
    // Execute backend logic rules that target this event, in sequence.
    // All invalid/disabled rules filtered by BE, so we just execute what's here.
    if (logicRules) {
      const logicRulesForEvent = logicRules.filter(
        (logicRule: any) => logicRule.trigger_event === event
      );
      const currentStepId = (internalState[_internalId]?.currentStep ?? {}).id;
      for (const logicRule of logicRulesForEvent) {
        if (canRunAction(logicRule, currentStepId, props, altMatchId)) {
          logicRan = true;

          if (toAwait) await toAwait;

          try {
            if (logicRule.server_side) {
              await runServerSideLogic(logicRule, client, _draft);
            } else {
              await runClientSideLogic(
                logicRule,
                client,
                extractedSharedCodeInfo,
                internalState[_internalId],
                connectorFields,
                props
              );
            }
          } catch (e: any) {
            const errorMessage =
              e.reason?.message ?? e.error?.message ?? e.message;
            handleRuleError(errorMessage, logicRule);
          }
        }
      }
      // Change event can happen too frequently to flush every time
      if (event !== 'change' && flush) await defaultClient.flushCustomFields();
    }

    // Flush field updates to backend before form completes
    if (event === 'form_complete') await defaultClient.flushCustomFields();

    return logicRan;
  };

  const getErrorCallback =
    (props1 = {}) =>
    (props2 = {}) =>
      runUserLogic('error', () => ({
        ...props1,
        ...props2
      }));

  const changeFormStep = (newKey: string, oldKey: string, load: boolean) => {
    const changed = changeStep(
      newKey,
      oldKey,
      steps,
      setStepKey,
      navigate,
      client,
      trackHashes.current
    );
    if (changed) {
      if (internalState[_internalId])
        internalState[_internalId].latestStepName = newKey;
      const backKey = load ? backNavMap[oldKey] : oldKey;
      updateBackNavMap({ [newKey]: backKey });
    }
    return changed;
  };

  const getNewStep = async (newKey: any) => {
    let newStep = steps[newKey];
    if (!newStep) return;

    const nextStep = getNextAuthStep(newStep);
    if (
      nextStep !== '' &&
      !initState.isTestEnv &&
      changeFormStep(nextStep, newKey, true)
    )
      return;
    const nextKey = nextStepKey(newStep.next_conditions, {
      elementType: 'step',
      elementIDs: [newStep.id]
    });
    if (nextKey && changeFormStep(nextKey, newKey, true)) {
      return;
    }

    newStep = JSON.parse(JSON.stringify(newStep));

    // This could be a redirect from Stripe following a successful payment checkout
    checkForPaymentCheckoutCompletion(
      steps,
      client,
      updateFieldValues,
      integrations?.stripe
    );

    // create fields only once and seal it
    let fields = internalState[_internalId]?.fields;
    if (!fields || !Object.isSealed(fields))
      fields = Object.seal(
        getAllFields(fieldKeys, Object.keys(hiddenFields), _internalId)
      );

    const newVisiblePositions = getVisiblePositions(newStep, _internalId);
    preloadStepFields(newStep, newVisiblePositions).catch(() => {});

    setFormInternalState(
      _internalId,
      {
        language: language ?? initState.language,
        currentStep: newStep,
        latestStepName: newStep.key,
        previousStepName: activeStep?.key ?? '',
        backNavMap,
        visiblePositions: newVisiblePositions,
        client,
        // Runs the full Generate Documents flow for the
        // `feathery.generateDocuments` logic-rule method, mirroring the
        // ACTION_GENERATE_ENVELOPES handler (generate → optional review modal →
        // envelope action) but resolving a promise instead of advancing the
        // action flow.
        generateEnvelopeFlow: async (action: Record<string, any>) => {
          // `actingAction` is the outcome to apply — the configured
          // envelope_action on the direct path, or the toolbar button the
          // filler pressed in the editor, where envelope_action is
          // 'open_in_editor' and carries no outcome of its own.
          const runEnvelopeAction = async (
            data: any,
            actingAction?: string,
            draft = false
          ) => {
            const envAction = actingAction ?? action.envelope_action;
            if (envAction === 'download' && data.files) {
              await downloadAllFileUrls(
                data.files,
                replaceTextVariables(action.envelope_zip_name)
              );
            } else if (envAction === 'save' && data.files) {
              let files = data.files;
              if (files.length === 1) files = files[0];
              const newValues = { [action.save_document_field_key]: files };
              updateFieldValues(newValues);
              client.submitCustom(newValues);
            } else if (!envAction || envAction === 'sign') {
              // `envAction` has to be passed: in the editor flow
              // action.envelope_action is 'open_in_editor', and the outcome is
              // the toolbar button the filler pressed.
              if (isDocusignSignAction(action, envAction)) {
                // DocuSign has no Feathery sign URL — its
                // {docusign_envelope_id, status} response is itself the
                // completion signal, so all that's left is telling the filler.
                showEnvelopeOutcome(
                  ENVELOPE_FLOW_TOAST_ID,
                  draft ? 'Saved as Draft' : 'Sent for Signature',
                  action.documents
                );
                return;
              }
              // Feathery hosted eSign: open the signing page. The action-flow
              // redirect/registerEvent variant is step-specific, so it's not
              // run for logic-rule invocations.
              //
              // Only ever opened as a signer: the batch returns the filler's
              // own token when they sign it, and there's nothing for them to
              // open when it doesn't.
              const signers = data.signers ?? [];
              const signerId = signers.find((s: any) => s.signer_id)?.signer_id;
              if (signerId) openTab(getSignUrl(signerId, action.redirect));
              else if (signers.some((s: any) => s.invited))
                showEnvelopeOutcome(
                  ENVELOPE_FLOW_TOAST_ID,
                  'Sent for Signature',
                  action.documents
                );
            }
          };
          const data = await client.generateEnvelopes(action);
          if (!data) throw new Error('Document generation failed');
          if (data.status === 'error') throw new Error(data.message);
          if (action.envelope_action === 'open_in_editor') {
            return await new Promise((resolve, reject) => {
              // What finalize returned, so the method resolves with the real
              // outcome ({files: [...]}) — the generate payload here only holds
              // {documents, expires_at}.
              let finalized: any;
              setReviewViewerPayload({
                payload: data,
                action,
                onFinalize: async ({
                  envelopes,
                  envelopeAction,
                  draft
                }: any) => {
                  const result = await client.finalizeEnvelopeReview(action, {
                    envelopes,
                    envelopeAction,
                    draft
                  });
                  if (!result) {
                    return { status: 'error', message: 'Finalize failed' };
                  }
                  if (result.status === 'error') return result;
                  await runEnvelopeAction(result, envelopeAction, draft);
                  finalized = result;
                  return result;
                },
                onComplete: () => {
                  setTimeout(() => setReviewViewerPayload(null), 500);
                  resolve(finalized);
                },
                // Closing the viewer without continuing has to settle the
                // promise, or an awaiting logic rule hangs for the life of the
                // page.
                onClose: () =>
                  reject(
                    new Error('Document review was closed before completing')
                  )
              });
            });
          }
          await runEnvelopeAction(data);
          return data;
        },
        // Lets a document-editor container, which runs its own signing action
        // outside this flow, report the outcome through the same toast.
        showEnvelopeOutcome: (label: string, documents?: string[]) =>
          showEnvelopeOutcome(ENVELOPE_CONTAINER_TOAST_ID, label, documents),
        fields,
        products: Object.seal(
          getSimplifiedProducts(integrations?.stripe, updateFieldValues, client)
        ),
        cart: Object.seal(
          getCart(integrations?.stripe, updateFieldValues, client)
        ),
        collaborator: Object.seal(
          new Collaborator(
            session?.collaborator?.template_label ?? '',
            session?.collaborator?.template_index ?? 0,
            session?.collaborator?.allowed ?? '',
            session?.collaborator?.whitelist ?? []
          )
        ),
        trackHashes: trackHashes.current,
        formId,
        formName,
        formRef,
        formSettings,
        getErrorCallback,
        navigate,
        inlineErrors,
        setInlineErrors,
        setUserProgress,
        setDataMappingState,
        steps,
        setStepKey,
        updateFieldOptions: (
          newOptions: FieldOptions,
          repeatIndex?: number
        ) => {
          Object.values(steps).forEach((step) =>
            updateStepFieldOptions(step, newOptions, repeatIndex)
          );
          setSteps(JSON.parse(JSON.stringify(steps)));
          updateStepFieldOptions(newStep, newOptions, repeatIndex);
        },
        updateFieldStyles: (fieldKey: string, newStyles: FieldStyles) => {
          Object.values(steps).forEach((step) =>
            updateStepFieldStyles(step, fieldKey, newStyles)
          );
          setSteps(JSON.parse(JSON.stringify(steps)));

          updateStepFieldStyles(newStep, fieldKey, newStyles);
        },
        updateFieldProperties: (
          fieldKey: string,
          newProperties: FieldProperties,
          onServar = false
        ) => {
          Object.values(steps).forEach((step) =>
            updateStepFieldProperties(step, fieldKey, newProperties, onServar)
          );
          setSteps(JSON.parse(JSON.stringify(steps)));

          updateStepFieldProperties(newStep, fieldKey, newProperties, onServar);
        },
        setFieldErrors: (
          errors: Record<string, string | { index: number; message: string }>
        ) => {
          Object.entries(errors).forEach(([fieldKey, error]) => {
            const { inlineErrors, setInlineErrors } =
              internalState[_internalId];
            let index = null;
            let message: string;
            // If the user provided an object for an error then use the specified index and message
            // This allows users to specify an error on an element in a repeated row
            if (typeof error === 'object') {
              index = error.index;
              message = error.message;
            } else {
              message = error;
            }
            setFormElementError({
              formRef,
              fieldKey,
              message,
              index,
              servarType: fields[fieldKey]?.type,
              errorType: formSettings.errorType,
              inlineErrors,
              setInlineErrors,
              triggerErrors: true
            });
          });
        },
        setCalendlyUrl: (url: string) => {
          if (integrations?.calendly?.metadata) {
            setIntegrations((integrations) => ({
              ...integrations,
              calendly: {
                ...integrations?.calendly,
                metadata: {
                  ...integrations?.calendly.metadata,
                  api_key: url
                }
              }
            }));
          }
        },
        sendDocusignEnvelope: async (params: SendDocusignParams) => {
          await Promise.all([
            client.flushCustomFields(),
            defaultClient.flushCustomFields()
          ]);
          return client.sendDocusignEnvelope(params);
        },
        getDocusignEnvelope: (params: GetDocusignEnvelopeParams) =>
          client.getDocusignEnvelope(params),
        updateDocusignEnvelope: (params: UpdateDocusignEnvelopeParams) =>
          client.updateDocusignEnvelope(params),
        uploadFileToEgnyte: (params: UploadFileToEgnyteParams) =>
          client.uploadFileToEgnyte(params),
        createEgnyteFolder: (params: CreateEgnyteFolderParams) =>
          client.createEgnyteFolder(params),
        getDocusignBrands: async () => {
          await Promise.all([
            client.flushCustomFields(),
            defaultClient.flushCustomFields()
          ]);
          return client.getDocusignBrands();
        },
        fillQuikForms: async ({
          fillType,
          docusignConnectionId,
          docusignCustomId,
          enableWetSign,
          documentTemplates,
          envelopes,
          attachments
        }: FillQuikParams) => {
          await Promise.all([
            client.flushCustomFields(),
            defaultClient.flushCustomFields()
          ]);
          const payload = await client.generateQuikEnvelopes({
            form_fill_type: fillType,
            review_action: 'sign',
            auth_user_id: docusignConnectionId,
            docusign_custom_id: docusignCustomId,
            enable_wet_sign: enableWetSign,
            document_template_attachments: documentTemplates,
            envelope_attachments: envelopes,
            attachments
          });
          if (payload.status === 'error') throw Error(payload.message);
          else if (fillType === 'html' && payload.html) {
            featheryWindow().QuikFeatherySubmitAction = () =>
              setShowQuikFormViewer(false);
            setQuikHTMLPayload(payload.html);
            setShowQuikFormViewer(true);
          } else if (fillType === 'pdf' && payload.files) {
            await downloadAllFileUrls(payload.files);
          }
        },
        runAIExtraction: async (
          extractionId: string,
          options: ExtractionActionOptions | boolean,
          // deprecated, pages should be in options
          pages?: PageSelectionInput
        ) => {
          if (!extractionId) {
            console.error('No extraction ID was passed');
            return;
          }

          // Auto-submit the extraction's file field(s) before running. Resilient
          // by design: a failure here shouldn't break a user's logic rule — the
          // extraction still runs against whatever's already been submitted.
          try {
            await submitExtractionFiles(
              extractionId,
              (typeof options === 'object' && options.variantId) || ''
            );
          } catch (e) {
            console.warn('Failed to auto-submit AI extraction files', e);
          }

          const data = await client.runAIExtraction({
            extractionId,
            options,
            pages,
            setPollFuserData,
            onStatusUpdate:
              typeof options === 'object' && options.waitForCompletion
                ? (pollData: any) =>
                    handleExtractionStatusUpdate(
                      extractionId,
                      (typeof options === 'object' && options.variantId) || '',
                      pollData,
                      true
                    )
                : undefined
          });
          if (data.status !== 'error') {
            const vals = data.data ?? {};
            updateFieldValues(vals);
            processFileValues(data.file_values);
          }
          return data;
        },
        runComputerAgent: async (
          agentId: string,
          options: RunComputerAgentOptions = {}
        ) => {
          const { userId } = initInfo();
          return client.runComputerAgent(agentId, {
            ...options,
            onComplete: (data: any) => {
              // A run that finishes after the submission changed must not
              // write into the new session's fields
              if (data.status === 'complete' && initInfo().userId === userId) {
                updateFieldValues(data.data ?? {});
                processFileValues(data.file_values);
              }
              options.onComplete?.(data);
            }
          });
        },
        forwardInboxEmail: async (options: ForwardInboxEmailOptions) => {
          return client.forwardInboxEmail({ options });
        }
      },
      // Avoid all these other obj props going through Object.assign which is not necessary.
      // It turns out that not doing so caused breakage on steps after the first step.
      // But for only fields it is fine and necessary.
      ['fields']
    );

    setUserLogicRunning(true);
    if (!formLoadRan.current) {
      formLoadRan.current = true;
      await runUserLogic('form_load');
    }
    await runUserLogic('load');
    setUserLogicRunning(false);

    if (trackHashes.current) {
      const newHash = getUrlHash();
      // This indicates user programmatically changed the step via the onLoad function
      // So loading of the old step must short circuit
      if (newHash && newStep.key !== newHash) return;
    }

    clearLoaders();
    const [curDepth, maxDepth] = recurseProgressDepth(steps, newKey);
    setCurDepth(curDepth);
    setMaxDepth(maxDepth);

    trackEvent(integrations, 'FeatheryStepLoad', newKey, formName);

    callbackRef.current = new CallbackQueue(newStep, setLoaders);
    focusRef.current = null;
    // Hydrate field descriptions
    newStep.servar_fields.forEach((field: any) => {
      const servar = field.servar;
      servar.name = replaceTextVariables(servar.name, field.repeat);
      const disabled = !fieldAllowedFromList(allowLists, servar.key);
      const props = field.properties;
      props.disabled = props.disabled || disabled;
      if (servar.required && props.disabled) servar.required = false;
    });
    const oldKey = activeStep?.key ?? '';
    // setActiveStep, apparently, must go after setting the callbackRef
    // because it triggers a new render, before this fn finishes execution,
    // which can cause onView to fire before the callbackRef is set
    setActiveStep(newStep);
    client
      .registerEvent({
        step_key: newStep.key,
        event: 'load',
        previous_step_key: oldKey
      })
      .catch(() => {});
  };

  const visiblePositions = useMemo(() => {
    if (!activeStep) return null;
    const visiblePositions = getVisiblePositions(activeStep, _internalId);

    if (formSettings.clearHideIfFields) {
      // Auto-reset hidden fields to defaults when they become hidden via hideIf rules.
      // This prevents stale data in fields that aren't visible, which could cause
      // validation errors or unexpected submissions when the field is shown again.
      // Only resets if value differs from default, minimizes unnecessary updates.
      const newFieldVals: Record<string, any> = {};
      activeStep.servar_fields.forEach((sf: any) => {
        const key = getPositionKey(sf);
        const flags = visiblePositions[key];
        const isRepeated = !!getRepeatedContainer(activeStep, sf);

        if (isRepeated) {
          const currentVal = fieldValues[sf.servar.key];
          if (!Array.isArray(currentVal)) return;
          const defaultVal = getDefaultFieldValue(sf);
          const defaultJson = JSON.stringify(defaultVal);
          let changed = false;
          const newArray = currentVal.map((val: any, i: number) => {
            if (
              i < flags.length &&
              !flags[i] &&
              JSON.stringify(val) !== defaultJson
            ) {
              changed = true;
              return defaultVal;
            }
            return val;
          });
          if (changed) newFieldVals[sf.servar.key] = newArray;
        } else if (!flags[0]) {
          const newVal = getDefaultFormFieldValue(sf);
          if (
            JSON.stringify(newVal) !== JSON.stringify(getFieldValue(sf).value)
          ) {
            newFieldVals[sf.servar.key] = newVal;
          }
        }
      });
      if (Object.keys(newFieldVals).length) {
        updateFieldValues(newFieldVals);
        client.submitCustom(newFieldVals);
      }
    }

    return visiblePositions;
  }, [activeStep, render, formSettings]);

  // keep internalState fresh
  if (internalState[_internalId]) {
    internalState[_internalId].setInlineErrors = setInlineErrors;
    internalState[_internalId].inlineErrors = inlineErrors;
    internalState[_internalId].logicRules = logicRules;
    if (visiblePositions) {
      internalState[_internalId].visiblePositions = visiblePositions;
    }
  }

  useEffect(() => {
    if (clientRef.current) return;

    clientRef.current = new FeatheryClient(
      formId,
      hasRedirected,
      _draft,
      _bypassCDN
    );
    const newClient = clientRef.current;
    let saveUrlParamsFormSetting = false;
    // render form without values first for speed
    const formPromise = newClient
      .fetchForm(initialValues, language)
      .then(async (data: any) => {
        setCustomErrorHandler();
        updateCustomCSS(data.custom_css ?? '');
        await updateCustomHead(data.custom_head ?? '');
        removeCustomErrorHandler();
        return data;
      })
      .then(({ steps, form_name: formNameResult, ...res }: any) => {
        setFormName(formNameResult);
        if (res.new_form_id) {
          setFormId(res.new_form_id);
        }
        steps = steps.reduce((result: any, step: any) => {
          result[step.key] = step;
          return result;
        }, {});
        setSteps(steps);
        if (res.completion_behavior === 'redirect' && res.redirect_url)
          initState.redirectCallbacks[_internalId] = () => {
            featheryWindow().location.href = replaceTextVariables(
              res.redirect_url,
              0
            );
          };
        if (res.save_url_params) saveUrlParamsFormSetting = true;
        const mappedSettings = mapFormSettingsResponse(res);
        setFormSettings({ ...formSettings, ...mappedSettings });
        // The upload tracker is keyed by formKey since upload reporting
        // happens in FeatheryClient, outside React state. Read the mapped
        // setting rather than the raw response so the default lives in one place
        setUploadIndicatorEnabled(
          newClient.formKey,
          mappedSettings.showFileUploadProgress
        );
        formOffReason.current = res.formOff ? CLOSED : formOffReason.current;
        setLogicRules(res.logic_rules);
        setSharedCodes((prev) => res.shared_codes || prev);
        extractionFileFields.current = res.extraction_file_fields || {};
        trackHashes.current =
          hashNavigation !== undefined ? hashNavigation : res.track_hashes;

        // Add any logic_rule.elements to viewElements so that onView called for then too.
        // Make sure there are no duplicate entries.
        if (res.logic_rules) {
          const newViewElements: string[] = [...viewElements];
          res.logic_rules.forEach((logicRule: LogicRule) => {
            if (logicRule.trigger_event === 'view') {
              logicRule.elements.forEach((elementId: any) => {
                if (!newViewElements.includes(elementId)) {
                  newViewElements.push(elementId);
                }
              });
            }
          });
          setViewElements(newViewElements);
        }

        if (res.connector_fields) {
          setConnectorFields(res.connector_fields);
        }

        if (res.production) installRecaptcha(steps);
        return steps;
      });
    // fetch values separately because this request
    // goes to Feathery origin, while the previous
    // request goes to our CDN
    newClient
      .fetchSession(formPromise, true)
      .then(([session, steps]: any) => {
        // A single-use link carries no form data until this device claims it
        if (session?.link?.confirm) {
          setLinkConfirmRequired(true);
          return;
        }

        if (!session || session.collaborator?.invalid)
          formOffReason.current = CLOSED;
        else if (session.link?.required) formOffReason.current = LINK_REQUIRED;
        else if (session.collaborator?.completed)
          formOffReason.current = COLLAB_COMPLETED;
        else if (session.collaborator?.direct_submission_disabled)
          formOffReason.current = COLLAB_DIRECT_DISABLED;
        else if (session.no_business_email)
          formOffReason.current = NO_BUSINESS_EMAIL;
        if (formOffReason.current) {
          setRender((render) => ({ ...render }));
          return;
        }

        if (!session.track_location && trackHashes.current) {
          // Clear URL hash on new session if not tracking location
          navigate(
            featheryWindow().location.pathname +
              featheryWindow().location.search,
            {
              replace: true
            }
          );
        }
        updateBackNavMap(session.back_nav_map);
        setIntegrations(session.integrations);

        setFieldKeys(session.servars);
        setHiddenFields(session.hidden_fields);
        setAllowLists([
          session.collaborator?.whitelist,
          session.collaborator?.blacklist
        ]);
        saveInitialValuesAndUrlParams({
          updateFieldValues,
          client: newClient,
          saveUrlParams: saveUrlParams || saveUrlParamsFormSetting,
          initialValues,
          steps,
          hiddenFields: session.hidden_fields
        });

        // User is authenticating. auth hook will set the initial stepKey once auth has finished
        if (authState.redirectAfterLogin || authState.hasRedirected || stepKey)
          return;

        const newKey = getInitialStep({
          initialStepId,
          steps,
          sessionCurrentStep: session.current_step_key,
          formId: _internalId
        });
        if (trackHashes.current) setUrlStepHash(navigate, steps, newKey);
        setStepKey(newKey);
      })
      .catch(async (error: any) => {
        console.warn(error);
        // Only the session resolves which submission an access link opens, so
        // without one there is no user id to write against. Block instead of
        // opening the origin step on an empty submission.
        if (initState.linkToken) {
          formOffReason.current = CLOSED;
          formOffMessage.current = LINK_SESSION_FAILED_MESSAGE;
          setRender((render) => ({ ...render }));
          return;
        }
        // Go to first step if origin fails
        const [data] = await formPromise;
        const newKey = (getOrigin as any)(data).key;
        if (trackHashes.current) setUrlStepHash(navigate, steps, newKey);
        else setStepKey(newKey);
      });
  }, [activeStep, setSteps, updateFieldValues]);

  useOfflineRequestHandler(client);

  useEffect(() => {
    if (!trackHashes.current) return;
    const hashKey = getUrlHash();
    if (hashKey in steps) {
      const scrollIntent = pendingScrollRef.current;
      setShouldScrollToTop(scrollIntent !== false);
      pendingScrollRef.current = null;
      setStepKey(hashKey);
    } else {
      pendingScrollRef.current = null;
    }
  }, [location]);

  useEffect(() => {
    // We set render to re-evaluate auth nav rules - but should only getNewStep if either the step or authId has changed.
    // Should not fetch new step if render was set for another reason
    if (
      stepKey &&
      (prevStepKey !== stepKey || prevAuthId !== authState.authId)
    ) {
      getNewStep(stepKey);
    }
  }, [stepKey, render]);

  // Updates field value and handles side effects like auto-adding repeated rows.
  // When repeat_trigger is 'set_value', automatically adds a new row when user fills
  // the last field in a repeated section (provides intuitive add-more UX).
  // If index is provided, field is treated as a repeated field with array-based values.
  const changeValue = (
    value: any,
    field: any,
    index: number | null = null,
    rerender = true,
    triggerErrors = true
  ) => {
    const updateValues = {};
    let repeatRowOperation;

    const servar = field.servar;
    let repeatContainer: Subgrid | undefined;
    if (servar.repeat_trigger === 'set_value') {
      const defaultValue = getDefaultFieldValue(field);
      const { value: previousValue, valueList } = getFieldValue(field);

      // Auto-add row when user fills the last field from empty state.
      // This creates a seamless experience where a new blank row appears automatically.
      const isPreviousValueDefaultArray =
        isEmptyArray(previousValue) && isEmptyArray(defaultValue);

      // Check if this is the last field in a repeated group
      const isLastRepeatedField = valueList && index === valueList.length - 1;

      repeatContainer = getRepeatedContainer(activeStep, field);
      if (
        isLastRepeatedField &&
        (previousValue === defaultValue || isPreviousValueDefaultArray) &&
        value !== defaultValue
      ) {
        repeatRowOperation = 'add';
      }
    }

    if (servar.type === 'integer_field' && value !== '')
      value = parseFloat(value);
    else if (servar.type === 'file_upload' && index !== null)
      // For file_upload in repeating rows
      // If empty array, insert null. Otherwise de-reference the single file in the array
      value = isEmptyArray(value) ? null : value[0];
    else if (
      servar.type === 'checkbox' &&
      // eslint-disable-next-line camelcase
      servar.metadata?.always_checked
    )
      value = true;

    // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    updateValues[servar.key] =
      index === null
        ? value
        : justInsert(fieldValues[servar.key] || [], value, index, field);

    const change = updateFieldValues(updateValues, { rerender, triggerErrors });
    if (repeatRowOperation === 'add' && repeatContainer)
      addRepeatedRow(repeatContainer);
    return change;
  };

  const getNextStepKey = (metadata: any) =>
    nextStepKey(activeStep.next_conditions, metadata);

  const submitStep = async (
    metadata: any,
    // Optional on purpose: undefined means the submitting element is NOT in a
    // repeated container, which must stay distinguishable from row 0.
    repeat: number | undefined,
    hasNext: boolean
  ): Promise<[Promise<any>] | undefined> => {
    const formattedFields = formatStepFields(
      activeStep,
      formSettings.saveHideIfFields ? null : visiblePositions,
      false
    );
    const trigger = lookUpTrigger(
      activeStep,
      metadata.elementIDs[0],
      metadata.elementType
    );
    // Legacy callback contract is numeric, so fall back to 0 here only.
    trigger.repeatIndex = repeat ?? 0;

    if (
      activeStep.servar_fields.find(
        ({ servar: { type } }: any) => type === 'payment_method'
      )
    ) {
      const errors: any = await setupStepPaymentMethod(formattedFields);
      const { errorMessage, errorField } = errors;
      if (errorMessage && errorField) {
        await setFormElementError({
          formRef,
          errorCallback: getErrorCallback({ trigger }),
          fieldKey: errorField.servar.key,
          message: errorMessage,
          servarType: errorField.servar.type,
          // Scope to the submitted row so a failure from one repeated row
          // doesn't render on its siblings. Undefined for a non-repeated field,
          // which stores a field-wide message the non-repeat renderer reads.
          index: repeat,
          errorType: formSettings.errorType,
          // Merge into the current error map rather than a fresh one, so
          // surfacing a payment error doesn't wipe other fields' errors.
          inlineErrors: internalState[_internalId]?.inlineErrors,
          setInlineErrors,
          triggerErrors: true
        });
        return;
      }
    }

    const customSubmitCode = async (beforeSubmit: boolean) => {
      let invalid = false;
      // Execute user-provided onSubmit function or submit rules if present
      if (
        await runUserLogic('submit', () => ({
          submitFields: formattedFields,
          trigger,
          beforeSubmit
        }))
      ) {
        // do validation check in case user has manually invalidated the step
        invalid = await setFormElementError({
          formRef,
          errorType: formSettings.errorType,
          // Need the latest accrued inlineErrors here.
          // This could have come potentially from multiple setFieldErrors calls.
          inlineErrors: internalState[_internalId].inlineErrors,
          setInlineErrors,
          triggerErrors: true
        });
      }
      return invalid;
    };

    let invalid = await customSubmitCode(true);
    if (invalid) return;

    const featheryFields = Object.entries(formattedFields).map(([key, val]) => {
      const servar = servarByKey.get(key);
      let newVal = val.value as any;
      newVal = Array.isArray(newVal)
        ? stripEmptyRepeatEntries(newVal, servar)
        : newVal;
      const field: Record<string, any> = { key, [val.type]: newVal };
      // Only the file submit path reads this. Setting it on every field would
      // change the shape of the JSON submit body, which carries these objects
      // verbatim.
      if (FILE_FIELD_TYPES.includes(val.type))
        field.repeated = Boolean(servar?.repeated);
      return field;
    });

    const stepPromise = client.submitStep(featheryFields, activeStep, hasNext);

    const fieldData: Record<string, any> = {};
    if (integrations?.segment?.metadata.track_fields)
      fieldData.segment = filterTrackedFields(
        formattedFields,
        integrations.segment.metadata.track_field_keys
      );
    if (integrations?.amplitude?.metadata.track_fields)
      fieldData.amplitude = filterTrackedFields(
        formattedFields,
        integrations.amplitude.metadata.track_field_keys
      );
    if (integrations?.['google-tag-manager']?.metadata.track_fields) {
      const gtmFields = filterTrackedFields(
        formattedFields,
        integrations['google-tag-manager'].metadata.track_field_keys
      );
      fieldData['google-tag-manager'] = Object.entries(gtmFields).reduce(
        (obj, [key, val]) => {
          obj[key] = val.value;
          return obj;
        },
        {} as Record<string, any>
      );
    }
    trackEvent(
      integrations,
      'FeatheryStepSubmit',
      activeStep.key,
      formName,
      fieldData
    );

    const hasSubmitAfter = logicRules?.some(
      (logicRule: any) =>
        logicRule.trigger_event === 'submit' &&
        isRunnableStepEventRule(logicRule, activeStep.id) &&
        logicRule.metadata?.after_click
    );
    if (hasSubmitAfter) {
      // If running submit logic rule after, must finish
      // submit data first
      await stepPromise;
      invalid = await customSubmitCode(false);
      if (invalid) return;
    }

    return [stepPromise];
  };

  // usePayments (Stripe)
  const [getCardElement, setCardElement] = usePayments();

  async function setupStepPaymentMethod(formattedFields: any) {
    // Stripe payment setup is disabled on a draft.  This is because setup is mostly
    // done on the BE either on step submit or on a collect payment action, both of which
    // are themselves disabled for draft.  Also mapped field data may also be configured
    // and would require a custom submit prior to payment setup (which is also disabled for draft).
    if (!_draft)
      for (let i = 0; i < activeStep.servar_fields.length; i++) {
        const field = activeStep.servar_fields[i];
        if (field.servar.type === 'payment_method') {
          const integrationData = integrations?.stripe;
          const actionData: ActionData = {
            pmField: field,
            client,
            formattedFields,
            updateFieldValues,
            integrationData,
            targetElement: getCardElement(field.servar.key)
          };
          const actionResult = await setupPaymentMethod(actionData);
          return actionResult ?? {};
        }
      }
    return {};
  }

  async function purchaseProductsAction(triggerElement: any) {
    // Stripe purchasing is disabled on a draft.  This is because payment is mostly
    // done on the BE using collect payment EP.  Mapped field data may also be configured
    // and would require a custom submit prior to payment (which is also disabled for draft).
    if (!_draft) {
      const trigger = {
        ...lookUpTrigger(activeStep, triggerElement.key, 'container'),
        repeatIndex: 0
      } as Trigger;
      const errorCallback = getErrorCallback({
        // could be container or button but model as button for the time being...
        trigger
      });
      // validate all step fields and buttons.  Must be valid before payment.
      const { invalid, inlineErrors: newInlineErrors } = validateElements({
        step: activeStep,
        visiblePositions,
        triggerErrors: true,
        errorType: formSettings.errorType,
        formRef,
        errorCallback,
        setInlineErrors,
        trigger
      });
      if (invalid) return false;

      // payment/checkout
      const pmField = activeStep.servar_fields.find(
        ({ servar: { type } }: any) => type === 'payment_method'
      );
      const errors = await purchaseCart({
        triggerElement,
        pmField,
        client,
        updateFieldValues,
        integrationData: integrations?.stripe,
        targetElement: pmField ? getCardElement(pmField.servar.key) : null
      });
      if (errors) {
        const { errorMessage, errorField } = errors;
        await setFormElementError({
          formRef,
          errorCallback,
          fieldKey: errorField.servar ? errorField.servar.key : errorField.id,
          message: errorMessage,
          servarType: errorField.servar ? errorField.servar.type : '',
          // Scope to the triggering element's row so a purchase failure in one
          // repeated row doesn't render on its siblings.
          index: triggerElement.repeat,
          errorType: formSettings.errorType,
          inlineErrors: newInlineErrors,
          setInlineErrors,
          triggerErrors: true
        });
        return false;
      }
    }
    return true;
  }

  // Handles stepping through the form, with special logic for form completion.
  // If redirectKey is empty, the form is complete and we finalize submission.
  // If redirectKey exists, navigate to next step (or handle terminal step completion).
  // Tracks whether navigation was explicit (user-initiated) vs implicit (logic-driven).
  async function goToNewStep({
    redirectKey,
    elementType,
    submitPromise,
    submitData = false,
    completionButton
  }: any) {
    let eventData: Record<string, any> = {
      step_key: activeStep.key,
      event: submitData ? 'complete' : 'skip'
    };

    eventData = { ...eventData, next_step_key: redirectKey };

    await callbackRef.current.all();
    const explicitNav =
      submitData || ['button', 'text', 'container'].includes(elementType);
    if (!redirectKey) {
      if (explicitNav) {
        showCompletionLoader(completionButton);
        try {
          if (submitPromise) {
            try {
              await submitPromise;
            } catch (error) {
              throw new Error(getSubmissionErrorMessage(error));
            }
          }

          requireSuccessfulFileUploads();

          // Block form completion if user is actively offline
          if (!(await verifyConnectivity())) {
            throw new Error(
              'You are offline. Please check your connection and try again.'
            );
          }

          // Note: We don't check dbHasRequest() here because:
          // 1. If submitPromise succeeded, the requests were already handled
          // 2. If submitPromise failed, we threw an error above and won't reach here
          // 3. Checking dbHasRequest() here would block manual retries after fixing network

          eventData.completed = true;
          await client.registerEvent(eventData).then(() => {
            setFinished(true);
            // Need to rerender when the session is marked complete so
            // LoginForm can render children
            session.form_completed = true;
            rerenderAllForms();
          });
        } finally {
          clearCompletionLoader();
        }
      }
    } else {
      const nextStep = steps[redirectKey];
      const authIntegration = getAuthIntegrationMetadata(integrations);
      const complete =
        isStepTerminal(nextStep) &&
        !isTerminalStepAuth(authIntegration, steps[stepKey].id);

      if (complete) showCompletionLoader(completionButton);

      try {
        if (complete && submitPromise) {
          try {
            await submitPromise;
          } catch (error) {
            throw new Error(getSubmissionErrorMessage(error));
          }
        } else if (submitPromise) {
          submitPromise.catch(() => {}); // Avoid unhandled rejections when allowing navigation
        }

        // Note: We don't block navigation when offline here to allow users to fill
        // out multi-step forms offline. API calls and custom logic will fail naturally
        // if they require network. Only block final form submission (see above).

        if (complete) {
          requireSuccessfulFileUploads();
          eventData.completed = true;
          // Form completion must run after since logic may depend on
          // presence of fully submitted data
          await client
            .registerEvent(eventData)
            .then(() => handleFormComplete());
        }
        if (!eventData.completed) client.registerEvent(eventData);
      } finally {
        if (complete) clearCompletionLoader();
      }
      if (internalState[_internalId])
        internalState[_internalId].latestStepName = redirectKey;
      updateBackNavMap({ [redirectKey]: activeStep.key });
      pendingScrollRef.current = explicitNav;

      if (trackHashes.current) {
        const newURL = getNewStepUrl(redirectKey);
        if (explicitNav) navigate(newURL);
        else navigate(newURL, { replace: true });
      } else {
        setShouldScrollToTop(explicitNav);
        pendingScrollRef.current = null;
        setStepKey(redirectKey);
      }
    }
  }

  const goToPreviousStep = async () => {
    await callbackRef.current.all();
    const prevStepKey = getPrevStepKey(activeStep, backNavMap);
    if (prevStepKey) {
      if (internalState[_internalId])
        internalState[_internalId].latestStepName = prevStepKey;
      pendingScrollRef.current = false;
      if (trackHashes.current) navigate(getNewStepUrl(prevStepKey));
      else {
        setShouldScrollToTop(false);
        pendingScrollRef.current = null;
        setStepKey(prevStepKey);
      }
    }
  };

  const setButtonLoader = async (button: any) => {
    const bp = button.properties;
    let loader: any = null;
    if (!bp.loading_icon) loader = <FeatherySpinner />;
    else if (bp.loading_icon_type === 'image/*') {
      loader = (
        <img
          style={{ width: '100%' }}
          src={bp.loading_icon}
          alt='Button Loader'
        />
      );
    } else if (bp.loading_icon_type === 'application/json') {
      const animationData = await fetch(bp.loading_icon).then((response) =>
        response.json()
      );
      loader = <Lottie animationData={animationData} />;
    }
    setLoaders((loaders: Record<string, any>) => ({
      ...loaders,
      [button.id]: {
        showOn: bp.show_loading_icon,
        loader,
        type: bp.loading_icon ? bp.loading_file_type : 'default',
        repeat: button.repeat
      }
    }));
  };

  const customClickSelectionState = (
    el: ClickActionElement
  ): null | boolean => {
    let state = null;
    for (const action of el.properties?.actions ?? []) {
      if ([ACTION_BACK, ACTION_NEXT].includes(action.type)) {
        return null;
      } else if (action.type === ACTION_STORE_FIELD) {
        if (state === null) state = false;
        let val;
        if (action.custom_store_value_type === 'field') {
          val = fieldValues[action.custom_store_value_field_key];
        } else val = action.custom_store_value;

        let destVal = fieldValues[action.custom_store_field_key];
        if (isNum(el.repeat)) {
          if (Array.isArray(val)) val = val[el.repeat] ?? '';
          if (Array.isArray(destVal)) destVal = destVal[el.repeat] ?? '';
        }
        if (!!val && destVal === val) state = true;
      } else if (action.type === ACTION_SELECT_PRODUCT_TO_PURCHASE) {
        if (state === null) state = false;
        const productId = getLiveOrTestProduct(
          action.product_id,
          integrations?.stripe
        );
        if (isProductInPurchaseSelections(productId)) state = true;
      }
    }
    return state;
  };

  const {
    isButtonActionRunning,
    updateButtonActionState,
    clearButtonActionState,
    setUserLogicRunning
  } = useCheckButtonAction(setButtonLoader, clearLoaders);

  const buttonOnClick = async (button: ClickActionElement) => {
    // Opened synchronously within the click's user-gesture call stack, before
    // any await, so the browser doesn't treat the popup as unsolicited.
    const actions = prioritizeActions(button.properties.actions ?? []);
    const preOpenedWindows = preOpenActionWindows(actions);

    if (!isButtonActionRunning()) {
      await setButtonLoader(button);
    }

    const setButtonError = (message: string) => {
      // Clear loaders before setting errors since buttons are disabled
      // when loaders are showing
      clearLoaders();
      // Set asynchronously since loaders need to unrender first. Publish the
      // pending write on internalState so programmatic callers (assistant
      // tools) can await it instead of racing the timer.
      const publication = new Promise<void>((resolve) => {
        setTimeout(() => {
          // Promise.resolve tolerates a non-promise return (e.g. a mocked
          // setFormElementError) so the publication always settles.
          Promise.resolve(
            setFormElementError({
              formRef,
              fieldKey: button.id,
              message,
              // Scope to the clicked button's repeat row, and merge into the
              // current error map (read fresh from internalState, since this
              // runs async) so a failure in one repeated row doesn't render on
              // every row or wipe unrelated field errors.
              index: button.repeat,
              errorType: formSettings.errorType,
              inlineErrors: internalState[_internalId]?.inlineErrors,
              setInlineErrors,
              triggerErrors: true
            })
          )
            .catch((err) => {
              // Still resolve so awaiters never hang, but leave a trail: a
              // swallowed failure here means assistant tools snapshot inline
              // errors believing the write landed.
              console.warn('Failed to set button error', err);
            })
            .then(() => resolve());
        }, 10);
      });
      const state = internalState[_internalId];
      if (state) {
        state.pendingInlineErrorPublish = publication;
        // Stop awaiting a settled publication on later calls.
        publication.then(() => {
          if (state.pendingInlineErrorPublish === publication)
            state.pendingInlineErrorPublish = undefined;
        });
      }
      return publication;
    };

    try {
      if (button.properties.captcha_verification && !initState.isTestEnv) {
        const invalid = await verifyRecaptcha(client);
        if (invalid) {
          closePreOpenedWindows(preOpenedWindows);
          setButtonError("You didn't pass CAPTCHA verification");

          return;
        }
      }

      const running = await runElementActions({
        actions,
        element: button,
        elementType: 'button',
        submit: button.properties.submit,
        setElementError: setButtonError,
        onAsyncEnd: () => clearLoaders(),
        preOpenedWindows
      });

      if (!running && !isButtonActionRunning()) {
        clearLoaders();
      }
    } catch (e: any) {
      closePreOpenedWindows(preOpenedWindows);
      // Clear the click lock so user can retry
      if (button.id) {
        elementClicks[button.id] = false;
      }
      clearButtonActionState();

      if (e) setButtonError(e.toString());
      else clearLoaders();
    }
  };

  const tableOnClick = async (table: any, payload: any) => {
    // show spinner if action is clicked
    if (payload.action) {
      const buttonKey = `${table.id}_${payload.rowIndex}_${payload.action}`;
      await setLoaders((loaders: Record<string, any>) => ({
        ...loaders,
        [buttonKey]: {
          showOn: 'on_button',
          loader: <FeatherySpinner />,
          type: 'default'
        }
      }));
    }
    try {
      await runElementActions({
        actions: [],
        element: table,
        elementType: 'table',
        triggerPayload: payload
      });
    } finally {
      clearLoaders();
      clearButtonActionState();
    }
  };

  // Orchestrates all actions triggered by a button/element click.
  // Runs validations and submission first (if submit=true), then executes click actions in order.
  // Prevents race conditions by locking element during execution and tracking global button state.
  // Some actions (Persona, Plaid, etc) require special handling: they pause execution and
  // provide a callback for continuing to the next action once they complete.
  const runElementActions = async ({
    actions,
    element,
    elementType,
    submit = false,
    setElementError = () => {},
    onAsyncEnd = () => {},
    textSpanStart,
    textSpanEnd,
    triggerPayload,
    preOpenedWindows: externalPreOpenedWindows
  }: {
    actions: any[];
    element: any;
    elementType: string;
    submit?: boolean;
    setElementError?: any;
    onAsyncEnd?: any;
    textSpanStart?: number | undefined;
    textSpanEnd?: number | undefined;
    triggerPayload?: Record<string, any>;
    preOpenedWindows?: Map<number, Window | null>;
  }) => {
    const id = element.id ?? '';
    const preOpenedWindows =
      externalPreOpenedWindows ?? new Map<number, Window | null>();
    // Prevent rapid re-clicks on the same element during async operations (file uploads, API calls)
    if (id && elementClicks[id]) {
      closePreOpenedWindows(preOpenedWindows);
      return;
    }
    elementClicks[id] = true;
    if (isButtonActionRunning()) {
      elementClicks[id] = false;
      closePreOpenedWindows(preOpenedWindows);
      return true;
    }

    updateButtonActionState(elementType, element, triggerPayload);

    // Prompted at the Next/Back action itself so validation and submission
    // decide the step is really leaving first. Full-page exits use beforeunload.
    let leaveDeclined = false;
    const confirmLeavingStep = () => {
      const proceed = confirmLeavingUnsavedWork(_internalId);
      leaveDeclined = !proceed;
      return proceed;
    };

    const metadata = {
      elementType,
      elementIDs: [element.id],
      start: textSpanStart,
      end: textSpanEnd
    };
    const trigger = {
      ...lookUpTrigger(
        activeStep,
        elementType === 'container' ? element.key : element.id,
        elementType,
        triggerPayload
      ),
      repeatIndex: element.repeat
    } as Trigger;
    let submitPromise: Promise<any> = Promise.resolve();

    // If the step is readOnly, don't run validation or submit
    if (submit && !readOnly) {
      // Do not proceed until user has gone through required flows
      if (
        !hasFlowActions(actions) &&
        requiredStepAction &&
        !flowCompleted.current
      ) {
        setElementError(REQUIRED_FLOW_ACTIONS[requiredStepAction]);
        elementClicks[id] = false;
        clearButtonActionState();

        closePreOpenedWindows(preOpenedWindows);
        return;
      }

      const pendingFileKeys = getPendingFileUploadKeys();
      if (pendingFileKeys.length) {
        await client.resetPendingFileUploads(pendingFileKeys);
      }

      // Clear any previous button error before re-validation (allows retry
      // after e.g. file upload errors). Scope the clear to this button's repeat
      // row and merge into the current map, and publish it (triggerErrors) so
      // the cleared state actually reaches the UI.
      if (submit && elementType === 'button') {
        setFormElementError({
          formRef,
          fieldKey: element.id,
          message: '', // Empty message clears the error
          index: element.repeat,
          errorType: formSettings.errorType,
          inlineErrors: internalState[_internalId]?.inlineErrors,
          setInlineErrors,
          triggerErrors: true
        });
      }

      const { invalid } = validateElements({
        step: activeStep,
        visiblePositions,
        triggerErrors: true,
        errorType: formSettings.errorType,
        formRef,
        errorCallback: getErrorCallback({ trigger }),
        setInlineErrors,
        trigger
      });

      if (invalid) {
        setAutoValidate(true);
        elementClicks[id] = false;
        clearButtonActionState();

        closePreOpenedWindows(preOpenedWindows);
        return;
      }

      // Don't try to complete the form on step submission if navigating to
      // a new step. The nav action goToNewStep will attempt to complete
      // it. If navigating but there is no step to navigate to, still attempt
      // to complete the form here since the user may leave before the
      // nav action event gets sent
      // A next action may specify an explicit target step (e.g. clicking a
      // stepper step); otherwise the next step is resolved from the step's
      // navigation conditions.
      const nextAction = actions.find(
        (action: any) => action.type === ACTION_NEXT
      );
      const hasNext =
        nextAction && (nextAction.next_step_key ?? getNextStepKey(metadata));

      let submissionResult: [Promise<any>] | undefined;
      try {
        submissionResult = await submitStep(
          metadata,
          // Pass the optional repeat through: `|| 0` would make a non-repeated
          // element look like row 0 and scope its errors to a row that the
          // non-repeat renderer never reads.
          element.repeat,
          !!hasNext
        );
      } catch (error) {
        submissionResult = [Promise.reject(error)] as [Promise<any>];
      }

      if (!submissionResult) {
        elementClicks[id] = false;
        clearButtonActionState();

        closePreOpenedWindows(preOpenedWindows);
        return;
      }

      const [stepPromise] = submissionResult;

      // Wrap step promise with error handler so file upload failures clean up UI state
      // and show user-friendly messages when awaited later (form completion, navigation, integrations)
      submitPromise = stepPromise.catch((error) => {
        const submissionErrorMessage = getSubmissionErrorMessage(error);
        elementClicks[id] = false;
        clearButtonActionState();
        setElementError(submissionErrorMessage);
        throw new Error(submissionErrorMessage);
      });

      // Ensure all rejection paths are marked handled so React Dev overlay
      // doesn't surface "Unhandled Runtime Error" when navigation is allowed
      submitPromise.catch(() => {});
    }

    if (!externalPreOpenedWindows) actions = prioritizeActions(actions);

    // Guards text/container callers if an async onAction or action logic rule breaks the gesture chain
    if (!externalPreOpenedWindows) {
      preOpenActionWindows(actions).forEach((win, idx) =>
        preOpenedWindows.set(idx, win)
      );
    }

    // The rest of the chain runs in a nested call over a sliced action array,
    // so the windows this click pre-opened for those actions have to be handed
    // across re-keyed to the slice, and dropped from this run's map, which
    // closes whatever it still owns once the loop ends. Without the handoff the
    // nested run pre-opens for itself - fine when it resumes from a fresh click
    // (a modal's save, the Quik viewer's submit), but the connect account chain
    // resumes straight off the OAuth result with no gesture left, so the browser
    // blocks the window and the next action reports a popup that was never
    // actually blocked. Windows already closed mean this run has finished and
    // the resume is carrying its own gesture, so it is left to open its own.
    const handOffPreOpenedWindows = (index: number) => {
      const remaining = new Map<number, Window | null>();
      let anyOpen = false;
      preOpenedWindows.forEach((win, idx) => {
        if (idx <= index) return;
        remaining.set(idx - index - 1, win);
        if (win && !win.closed) anyOpen = true;
      });
      if (!anyOpen) return undefined;
      remaining.forEach((_, idx) => preOpenedWindows.delete(idx + index + 1));
      return remaining;
    };

    const flowOnSuccess = (index: number) => async () => {
      flowCompleted.current = true;
      elementClicks[id] = false;
      clearButtonActionState();

      const running = await runElementActions({
        actions: actions.slice(index + 1),
        element,
        elementType,
        submit,
        setElementError,
        onAsyncEnd,
        textSpanStart,
        textSpanEnd,
        triggerPayload,
        preOpenedWindows: handOffPreOpenedWindows(index)
      });
      if (!running) onAsyncEnd();
    };
    const actionTypes = actions.map((action) => action.type);
    const runAction = (beforeClickActions: boolean) =>
      runUserLogic(
        'action',
        () => ({
          trigger,
          beforeClickActions,
          actions: actionTypes,
          actionData: actions
        }),
        elementType === 'container' ? element.id : element.properties?.link_id,
        submitPromise,
        // Only need to flush data if might race against
        // click actions
        !!(beforeClickActions && actions.length)
      );

    await runAction(true);

    let i: number;
    const hasExtractions = actions.some(
      (action) => action.type === ACTION_AI_EXTRACTION && !action.run_async
    );
    if (hasExtractions) {
      initializeActionExtractions(actions);
    }
    const hasEnvelopeGeneration = actions.some(
      (action) => action.type === ACTION_GENERATE_ENVELOPES
    );
    if (hasEnvelopeGeneration) {
      initializeEnvelopeGeneration(actions);
    }
    for (i = 0; i < actions.length; i++) {
      const action = actions[i];
      const type = action.type;

      if (type === ACTION_ADD_REPEATED_ROW) {
        const container = getContainerById(activeStep, action.repeat_container);
        addRepeatedRow(container, action.max_repeats);
      } else if (type === ACTION_REMOVE_REPEATED_ROW) {
        const container = getContainerById(activeStep, action.repeat_container);
        removeRepeatedRow(element, container);
      } else if (type === ACTION_TRIGGER_PERSONA) {
        await Promise.all([submitPromise, client.flushCustomFields()]);
        const personaMeta = integrations?.persona.metadata ?? {};
        triggerPersona(
          personaMeta,
          flowOnSuccess(i),
          setElementError,
          updateFieldValues,
          client
        );
        break;
      } else if (type === ACTION_ALLOY_VERIFY_ID) {
        await Promise.all([submitPromise, client.flushCustomFields()]);
        await verifyAlloyId(action, integrations?.alloy, flowOnSuccess(i));
        break;
      } else if (type === ACTION_SCHWAB_CREATE_CONTACT) {
        await Promise.all([submitPromise, client.flushCustomFields()]);
        await createSchwabContact(client, setElementError);
        break;
      } else if (type === ACTION_TRIGGER_PLAID) {
        await submitPromise;
        await openPlaidLink(
          client,
          flowOnSuccess(i),
          () => onAsyncEnd(),
          updateFieldValues,
          action,
          (err?: string) =>
            setElementError(err || 'Plaid was unable to fetch your data')
        );
        break;
      } else if (type === ACTION_TRIGGER_ARGYLE) {
        await Promise.all([submitPromise, client.flushCustomFields()]);
        await openArgyleLink(client, flowOnSuccess(i), integrations?.argyle);
        break;
      } else if (type === ACTION_TRIGGER_FLINKS) {
        await submitPromise;
        await openFlinksConnect(
          client,
          flowOnSuccess(i),
          (err?: string) => setElementError(err || 'Please connect Flinks'),
          integrations?.flinks,
          updateFieldValues
        );
        break;
      } else if (type === ACTION_CONNECT_ACCOUNT) {
        // isButtonActionRunning() only gates button/table triggers, so two
        // non-button triggers (e.g. two containers) can both reach here
        // before either's modal is dismissed. Without this, the second
        // trigger's openConnectAccountModal call would silently discard the
        // first trigger's flow-advance closure. Ignore this trigger instead;
        // the shared post-loop cleanup below still releases its click lock
        // and closes its own pre-opened popup.
        // Only covers triggers that open a modal: the ref is set by
        // openConnectAccountModal, so a provider that connects without one
        // races here exactly as it did before the modal existed.
        if (connectAccountModalRef.current) break;

        await Promise.all([submitPromise, client.flushCustomFields()]);
        const popup = preOpenedWindows.get(i) ?? null;
        preOpenedWindows.delete(i);
        const provider = action.provider;
        // Not always an email: a provider with no user identity records only
        // that a connection exists. Either way a value here means connected.
        const connectionKey = connectionFieldKey(provider);

        const alreadyConnected = !!fieldValues[connectionKey];
        let connected = false;
        try {
          if (alreadyConnected) {
            popup?.close();
          } else {
            const result = await runOAuthPopup(client, provider, popup);
            updateFieldValues({
              [connectionKey]: hasEmailIdentity(provider)
                ? result.account_email ?? ''
                : 'true'
            });
          }
          connected = true;
        } catch (error) {
          elementClicks[id] = false;
          clearButtonActionState();
          setElementError(
            error instanceof Error
              ? error.message
              : 'Unable to connect your account.'
          );
          onAsyncEnd();
        }
        // Deliberately outside the try: the modal's setup UI is what advances
        // the flow, and advancing runs every remaining action in the chain.
        // A failure in one of those is that action's error, not a failure to
        // connect, so it must not land in the catch above and get relabelled
        // "Unable to connect your account."
        if (connected) {
          // A repeat click on an already-connected button still opens the
          // modal - it is the only route to "Change account". On a fresh
          // connect the modal is worth showing only for a provider with setup
          // to collect; otherwise connecting is the whole job and the flow
          // continues straight away.
          if (alreadyConnected || CONFIG_COMPONENTS[provider]) {
            // The flow advances from the modal's onSaved, which only a
            // provider's config component calls - the respondent has not
            // finished configuring the account yet.
            openConnectAccountModal({
              provider,
              onFlowSuccess: flowOnSuccess(i),
              onAsyncEnd
            });
          } else {
            await flowOnSuccess(i)();
          }
        }
        break;
      } else if (type === ACTION_URL) {
        let url = replaceTextVariables(action.url, element.repeat);
        if (url) {
          if (!url.includes(':')) url = 'https://' + url;
          if (action.open_tab) {
            const preOpened = preOpenedWindows.get(i);
            if (preOpened) {
              preOpened.location.href = url;
              preOpenedWindows.delete(i);
            } else {
              openTab(url);
            }
          }
        }
        if (!action.open_tab) {
          const eventData: Record<string, any> = {
            step_key: activeStep.key,
            next_step_key: '',
            event: submit ? 'complete' : 'skip',
            completed: true
          };
          await client.registerEvent(eventData);
          featheryWindow().location.href = url;
        }
      } else if (type === ACTION_SEND_SMS_MESSAGE) {
        const phoneNum = fieldValues[action.phone_target_field_key] as string;
        if (validators.phone(phoneNum)) {
          try {
            await client.sendSMSMessage(phoneNum, action.sms_message);
          } catch (e) {
            setElementError((e as Error).message);
            break;
          }
        } else {
          setElementError('Your phone number is invalid or requested too much');
          break;
        }
      } else if (type === ACTION_SEND_SMS_CODE) {
        const phoneNum = fieldValues[action.auth_target_field_key] as string;
        if (validators.phone(phoneNum)) {
          try {
            await Auth.sendSms(phoneNum, client);
          } catch (e) {
            setElementError((e as Error).message);
            break;
          }
        } else {
          setElementError('Your phone number is invalid or requested too much');
          break;
        }
      } else if (type === ACTION_SEND_EMAIL_CODE) {
        const emailAddress = fieldValues[action.email_field_key] as string;
        if (validators.email(emailAddress)) {
          try {
            await client.sendEmailOTP(emailAddress);
          } catch (e) {
            setElementError((e as Error).message);
            break;
          }
        } else {
          setElementError('Your email address is invalid');
          break;
        }
      } else if ([ACTION_VERIFY_EMAIL, ACTION_VERIFY_SMS].includes(type)) {
        const pinKey = action.auth_target_field_key;
        const pin = fieldValues[pinKey] as string;
        const params = { fieldVal: pin, featheryClient: client };
        let hasErr = false;
        const prom =
          type === ACTION_VERIFY_EMAIL
            ? client.verifyOTP(pin, 'email-otp')
            : Auth.verifySMSOTP(params);
        await prom.catch((e: Error) => {
          let message = '';
          if (e.message === 'Please try again')
            message = 'Your code is invalid';
          else if (e.message in initState.defaultErrors)
            message = initState.defaultErrors[e.message];
          else message = e.message;
          setElementError(message);
          hasErr = true;
        });
        if (hasErr) break;

        client.submitCustom({ [pinKey]: pin });
        authState.redirectAfterLogin = true;
      } else if (type === ACTION_SEND_MAGIC_LINK) {
        const email = fieldValues[action.auth_target_field_key] as string;
        if (validators.email(email)) {
          try {
            await Auth.sendMagicLink(email);
          } catch (e) {
            setElementError((e as Error).message);
            break;
          }
        } else {
          setElementError('A valid email is needed to send your magic link.');
          break;
        }
      } else if (type === ACTION_OAUTH_LOGIN) {
        const auth = await Auth.oauthRedirect(action.oauth_type, client);
        if (auth && auth.result) {
          const loginStep: any = getLoginStep(steps, integrations);
          if (loginStep) {
            await goToNewStep({
              redirectKey: loginStep.key
            });
          }
        }
      } else if (type === ACTION_LOGOUT) await Auth.inferAuthLogout();
      else if (type === ACTION_NEW_SUBMISSION) await updateUserId(uuidv4());
      else if (type === ACTION_NEXT) {
        if (!confirmLeavingStep()) break;
        await goToNewStep({
          redirectKey: action.next_step_key ?? getNextStepKey(metadata),
          elementType: metadata.elementType,
          submitData: submit,
          submitPromise,
          completionButton:
            elementType === 'button' ? (element as ClickActionElement) : null
        });
      } else if (type === ACTION_BACK) {
        if (!confirmLeavingStep()) break;
        await goToPreviousStep();
      } else if (type === ACTION_PURCHASE_PRODUCTS) {
        const actionSuccess = await purchaseProductsAction(element);
        if (!actionSuccess) break;
      } else if (type === ACTION_SELECT_PRODUCT_TO_PURCHASE) {
        addToCart(action, updateFieldValues, integrations?.stripe, client);
      } else if (type === ACTION_REMOVE_PRODUCT_FROM_PURCHASE) {
        removeFromCart(action, updateFieldValues, integrations?.stripe, client);
      } else if (type === ACTION_VERIFY_COLLABORATOR) {
        const val = fieldValues[action.email_field_key] as string;
        if (!validators.email(val)) {
          setElementError(`${val} is an invalid email`);
          break;
        }
        const { valid } = await client.verifyCollaborator(val);
        if (!valid) {
          setElementError('Invalid form collaborator');
          break;
        }
      } else if (type === ACTION_INVITE_COLLABORATOR) {
        await Promise.all([submitPromise, client.flushCustomFields()]);
        // Invited collaborators is a mixed list of emails and/or user group names (comma sep or array)
        // An unfilled repeat row leaves a blank entry behind (`''`, or `null` for
        // select/signature fields), and a trailing comma does the same for the CSV
        // form. Those aren't invitees, so drop them before the required check -
        // otherwise a filled field reads as non-empty and we send the blank on.
        const invitees = toList(fieldValues[action.email_field_key], true)
          .map((invitee: any) =>
            typeof invitee === 'string' ? invitee.trim() : invitee
          )
          .filter(Boolean);
        if (!invitees.length) {
          setElementError('Collaborators required');
          break;
        }
        // BE validates emails and user groups
        try {
          const res = await client.inviteCollaborator(
            invitees,
            action.template_id
          );
          const collabKey = action.collaborator_field_key;
          if (res && res.ok && collabKey) {
            const { collaborators } = res.payload;
            const newVals = {
              [collabKey]: collaborators.map((c: any) => c.id)
            };
            updateFieldValues(newVals);
            await client.submitCustom(newVals);
          }
        } catch (e: any) {
          setElementError((e as Error).message);
          break;
        }
      } else if (type === ACTION_SET_TASK_STATUS) {
        await Promise.all([submitPromise, client.flushCustomFields()]);
        try {
          await client.setTaskStatus(action.template_id, action.task_status_id);
        } catch (e: any) {
          setElementError((e as Error).message);
          break;
        }
      } else if (type === ACTION_REWIND_COLLABORATION) {
        await Promise.all([submitPromise, client.flushCustomFields()]);
        try {
          await client.rewindCollaboration(
            action.template_id,
            action.email_field_key
          );
        } catch (e: any) {
          setElementError((e as Error).message);
          break;
        }
      } else if (type === ACTION_AI_EXTRACTION) {
        await Promise.all([submitPromise, client.flushCustomFields()]);
        const extractions = [];
        try {
          while (i < actions.length) {
            const curAction = actions[i];
            if (
              curAction.type === ACTION_AI_EXTRACTION &&
              // process the first extraction and any after if not run_sequential
              (extractions.length === 0 || !curAction.run_sequential)
            ) {
              // Auto-submit the extraction's file field(s) before running,
              // regardless of this button's "Validate & Submit Step" toggle.
              await submitExtractionFiles(
                curAction.extraction_id,
                curAction.variant_id || ''
              );
              extractions.push(
                client.runAIExtraction({
                  extractionId: curAction.extraction_id,
                  options: {
                    waitForCompletion: !curAction.run_async,
                    variantId: curAction.variant_id,
                    meetingUrl: fieldValues[curAction.meeting_url_field_key]
                  },
                  undefined,
                  setPollFuserData,
                  onStatusUpdate: (pollData: any) => {
                    handleExtractionStatusUpdate(
                      curAction.extraction_id,
                      curAction.variant_id || '',
                      pollData
                    );
                    if (pollData.status === 'complete' && pollData.data) {
                      updateFieldValues(pollData.data);
                      processFileValues(pollData.file_values);
                    }
                  }
                })
              );
              // set current extraction to pending
              updateExtractionInAction(
                curAction.extraction_id,
                curAction.variant_id || '',
                { status: 'incomplete' }
              );
              i++;
            } else {
              i--;
              break;
            }
          }
          const data = await Promise.all(extractions);
          const errorEntry = data.find((entry) => entry.status === 'error');
          if (errorEntry) {
            setElementError(errorEntry.message);
            break;
          } else {
            data.forEach((entry) => {
              if (entry.data) updateFieldValues(entry.data);
              processFileValues(entry.file_values);
            });
          }
        } catch (e: any) {
          setElementError((e as Error).message);
          break;
        }
      } else if (type === ACTION_GENERATE_ENVELOPES) {
        // TODO (tyler): extract this whole generate-envelopes branch (and the
        // other click-action branches in runElementActions) into its own
        // module. It is too much logic to keep bloating the Form component
        // with, and runEnvelopeAction plus the review-viewer wiring below are
        // self-contained enough to move behind a small interface.
        const envelopeId = `envelope-${i}`;
        updateEnvelopeGeneration(envelopeId, { status: 'incomplete' });
        await Promise.all([submitPromise, client.flushCustomFields()]);
        // Shared with the editor's finalize response: runs the same
        // sign-redirect/download/save handling the direct path always ran
        // immediately off the generate response. `actingAction` is the outcome
        // to apply — the configured envelope_action on the direct path, or the
        // toolbar button the filler pressed in the editor (where
        // envelope_action is 'open_in_editor' and carries no outcome itself).
        // Self-guards on a container editor, which hands the envelope to a
        // document-editor container instead of running the action.
        const runEnvelopeAction = async (
          data: any,
          actingAction?: string,
          draft = false
        ) => {
          if (editorContainerId(action)) return;
          const envAction = actingAction ?? action.envelope_action;
          if (!envAction || envAction === 'sign') {
            if (isDocusignSignAction(action, envAction)) {
              // DocuSign sign has no Feathery sign URL to redirect to — the
              // `{docusign_envelope_id, status}` response (already validated
              // for errors above) is itself the completion signal, so just
              // continue the flow. `envAction` has to be passed: in the editor
              // flow action.envelope_action is 'open_in_editor', and the
              // outcome is the toolbar button the filler pressed.
              //
              // Create Draft finalizes as a sign too, so the label comes off
              // the request — a draft is saved in the sender's DocuSign
              // account, not delivered to anyone. Not off the response: the
              // poll endpoint overwrites its status with "complete", so
              // DocuSign's own "created" never reaches here.
              showEnvelopeOutcome(
                envelopeId,
                draft ? 'Saved as Draft' : 'Sent for Signature',
                action.documents
              );
              return;
            }
            // One entry comes back per signable envelope, carrying an id only
            // when the filler signs it first. One signer link covers the rest
            // of the batch they can sign.
            const responseSigners = data.signers ?? [];
            const matchedSigner = responseSigners.find((s: any) => s.signer_id);
            if (!matchedSigner) {
              // Nothing in the batch for the filler to sign themselves.
              if (responseSigners.some((s: any) => s.invited))
                showEnvelopeOutcome(
                  envelopeId,
                  'Sent for Signature',
                  action.documents
                );
              return;
            }
            // Sign files
            const url = getSignUrl(matchedSigner.signer_id, action.redirect);
            if (action.redirect) {
              const eventData: Record<string, any> = {
                step_key: activeStep.key,
                next_step_key: '',
                event: submit ? 'complete' : 'skip',
                completed: true
              };
              await client.registerEvent(eventData);
              featheryWindow().location.href = url;
            } else openTab(url);
          } else if (envAction === 'download' && data.files) {
            // Download files directly
            await downloadAllFileUrls(
              data.files,
              replaceTextVariables(action.envelope_zip_name)
            );
          } else if (envAction === 'save' && data.files) {
            let files = data.files;
            if (files.length === 1) files = files[0];
            const newValues = { [action.save_document_field_key]: files };
            updateFieldValues(newValues);
            client.submitCustom(newValues);
          }
        };
        try {
          const data = await client.generateEnvelopes(action);
          // A missing response is a failure, not a success: _fetch resolves
          // undefined on a network blip / 403 / 409, and reading .status off it
          // would throw a raw TypeError instead of showing the user an error.
          if (!data) {
            updateEnvelopeGeneration(envelopeId, { status: 'error' });
            setElementError('Failed to generate documents. Please try again.');
            break;
          }
          if (data.status === 'error') {
            updateEnvelopeGeneration(envelopeId, { status: 'error' });
            setElementError(data.message);
            break;
          }
          updateEnvelopeGeneration(envelopeId, { status: 'complete' });

          const containerId = editorContainerId(action);
          if (containerId) {
            const refreshDetail = {
              containerId,
              documents: action.documents ?? [],
              envelopes: data.envelopes ?? []
            };
            const win = featheryWindow() as any;
            win.__featheryDocxEditorDrafts = {
              ...(win.__featheryDocxEditorDrafts ?? {}),
              [containerId]: refreshDetail
            };
            // Tell any mounted document-editor container to reload the freshly
            // generated envelope (needed when the editor is on the same step as
            // the button; a different-step editor consumes the stored draft on
            // mount).
            win.dispatchEvent(
              new CustomEvent('feathery-docx-editor-refresh', {
                detail: refreshDetail
              })
            );
          } else if (action.envelope_action === 'open_in_editor') {
            // Open the review viewer with the generated envelopes instead of
            // running the download/save/sign handling immediately; it runs
            // once the user hits Continue and finalize succeeds. Mutually
            // exclusive with the container editor above, which presents its own
            // editing surface (and needs the plain generate response).
            setReviewViewerPayload({
              payload: data,
              action,
              onFinalize: async ({
                envelopes,
                envelopeAction,
                draft
              }: {
                envelopes: { envelopeId: string }[];
                envelopeAction: 'sign' | 'fill' | 'download' | 'save';
                draft: boolean;
              }) => {
                const result = await client.finalizeEnvelopeReview(action, {
                  envelopes,
                  envelopeAction,
                  draft
                });
                // A missing result is a failure, not a success: _fetch
                // resolves undefined on a network blip / 403 / 409, and
                // `result?.status` would let that fall through to the sign
                // redirect (or throw a raw TypeError in the save branch) as
                // if finalize had succeeded.
                if (!result)
                  return {
                    status: 'error',
                    message: 'Failed to finalize documents. Please try again.'
                  };
                if (result.status === 'error') return result;
                await runEnvelopeAction(result, envelopeAction, draft);
                return result;
              },
              onComplete: () => {
                flowOnSuccess(i)().then(() => {
                  setTimeout(() => setReviewViewerPayload(null), 500);
                });
              }
            });
            break;
          } else {
            await runEnvelopeAction(data);
          }
        } catch (e: any) {
          updateEnvelopeGeneration(envelopeId, { status: 'error' });
          setElementError((e as Error).message);
          break;
        }
      } else if (type === ACTION_GENERATE_QUIK_DOCUMENTS) {
        await Promise.all([submitPromise, client.flushCustomFields()]);
        try {
          const payload = await client.generateQuikEnvelopes(action);
          if (payload.status === 'error') setElementError(payload.message);
          else if (action.form_fill_type === 'html' && payload.html) {
            featheryWindow().QuikFeatherySubmitAction = () => {
              flowOnSuccess(i)().then(() => {
                // Avoid flicker of step before it navigates
                setTimeout(() => setShowQuikFormViewer(false), 500);
              });
            };
            featheryWindow().QuikFeatheryBackAction = () => {
              // Turn off loaders if user leaves the Quik viewer without
              // submitting
              clearLoaders();
            };
            setQuikHTMLPayload(payload.html);
            setShowQuikFormViewer(true);
            break;
          } else if (action.form_fill_type === 'pdf' && payload.files) {
            await downloadAllFileUrls(
              payload.files,
              replaceTextVariables(action.envelope_zip_name)
            );
          }
        } catch (e: any) {
          setElementError((e as Error).message);
          break;
        }
      } else if (type === ACTION_STORE_FIELD) {
        const key = action.custom_store_field_key;
        if (!key) continue;

        let val;
        if (action.custom_store_value_type === 'field') {
          val = fieldValues[action.custom_store_value_field_key];
        } else val = action.custom_store_value;

        // Nested find statements return an item from the outer collection, so
        // short circuit the "some" statement once the field has been found
        let servar: any;
        Object.values(steps).some((step) => {
          const field = step.servar_fields.find(
            (field: any) => field.servar.key === key
          );
          if (field) {
            servar = field.servar;
            return true;
          }
        });

        if (isNum(element.repeat) && Array.isArray(val))
          val = val[element.repeat] ?? '';

        const hiddenFieldType = hiddenFields[key];
        val = hiddenFieldType
          ? castHiddenVal(hiddenFieldType, val)
          : castServarVal(servar?.type, val);

        let destVal = fieldValues[key] as any[];
        if (servar?.repeated) destVal = destVal[element.repeat ?? 0];
        const setToDefaultValue =
          action.toggle && JSON.stringify(destVal) === JSON.stringify(val);
        if (setToDefaultValue) {
          // could be a hidden field
          val = servar ? getDefaultFieldValue({ servar }) : '';
        }

        if (servar?.repeated) {
          destVal = [...(fieldValues[key] as any[])];
          const curIndex = element.repeat ?? 0;
          // Repeat index already set via click if retain_click_value
          if (!action.retain_click_value) destVal[curIndex] = val;
          if (action.repeat_single) {
            const defaultVal = getDefaultFieldValue({ servar });
            for (let i = 0; i < destVal.length; i++) {
              if (i !== curIndex) destVal[i] = defaultVal;
            }
          }

          val = destVal;
        } else if (action.retain_click_value) continue; // Field already set via click

        const newValues = { [key]: val };
        updateFieldValues(newValues, { clearErrors: false });
        client.submitCustom(newValues);
      } else if (type === ACTION_TELESIGN_SILENT_VERIFICATION) {
        const phoneNum = fieldValues[
          action.telesign_target_field_key
        ] as string;
        if (validators.phone(phoneNum)) {
          try {
            const silentVeriResult: boolean =
              await client.telesignSilentVerification(phoneNum);

            // set specified field value to the result
            const key = action.telesign_status_field_key;
            const newValues = {
              [key]: silentVeriResult
            };
            updateFieldValues(newValues, { clearErrors: false });
            client.submitCustom(newValues);
          } catch (e) {
            setElementError((e as Error).message);
            break;
          }
        } else {
          setElementError('Your phone number is invalid');
          break;
        }
      } else if (type === ACTION_TELESIGN_PHONE_TYPE) {
        const phoneNum = fieldValues[
          action.telesign_target_field_key
        ] as string;
        if (validators.phone(phoneNum)) {
          try {
            const phoneType: string = await client.telesignPhoneType(phoneNum);

            // set specified field value to the result
            const key = action.telesign_status_field_key;
            const newValues = {
              [key]: phoneType
            };
            updateFieldValues(newValues, { clearErrors: false });
            client.submitCustom(newValues);
          } catch (e) {
            setElementError((e as Error).message);
            break;
          }
        } else {
          setElementError('Your phone number is invalid');
          break;
        }
      } else if (
        [ACTION_TELESIGN_VOICE_OTP, ACTION_TELESIGN_SMS_OTP].includes(type)
      ) {
        const phoneNum = fieldValues[
          action.telesign_target_field_key
        ] as string;
        if (validators.phone(phoneNum)) {
          try {
            await client.telesignSendOTP(
              phoneNum,
              type === ACTION_TELESIGN_VOICE_OTP ? 'voice' : 'sms'
            );
          } catch (e) {
            setElementError((e as Error).message);
            break;
          }
        } else {
          setElementError('Your phone number is invalid');
          break;
        }
      } else if (type === ACTION_TELESIGN_VERIFY_OTP) {
        const pinKey = action.telesign_target_field_key;
        const pin = fieldValues[pinKey] as string;
        try {
          const pinMatch = await client.telesignVerifyOTP(pin);
          if (!pinMatch) {
            setElementError('Invalid code. Please try again');
            break;
          } else client.submitCustom({ [pinKey]: pin });
        } catch (e) {
          setElementError((e as Error).message);
          break;
        }
      } else if (type === ACTION_START_DATA_MAPPING) {
        setDataMappingState({ show: true, hubs: action.mapping_hubs ?? [] });
      }
    }

    closePreOpenedWindows(preOpenedWindows);

    if (i < actions.length) {
      elementClicks[id] = false;
      clearButtonActionState();

      // The user chose to keep their unsaved work, so nothing is pending.
      // Return falsy so the caller clears the button loader.
      if (leaveDeclined) return;

      return true;
    }

    await runAction(false);
    elementClicks[id] = false;
    clearButtonActionState();
  };

  const fieldOnChange =
    ({ fieldID, fieldKey, servarId, elementRepeatIndex = 0 }: any) =>
    ({
      triggerType = 'field',
      submitData = false,
      integrationData = {},
      // Other servars changed by the same interaction, e.g. the city/state/
      // country an address autocomplete fills in alongside the address line
      relatedServarIds = [],
      // Multi-file upload is not a repeated row but a repeated field
      valueRepeatIndex = null
    } = {}) => {
      if (eventHasUserLogic('change')) {
        callbackRef.current.addCallback(
          runUserLogic('change', () => ({
            trigger: {
              id: fieldKey,
              _servarId: servarId,
              _relatedServarIds: relatedServarIds,
              repeatIndex: elementRepeatIndex,
              type: triggerType
            },
            integrationData,
            valueRepeatIndex
          }))
        );
        setShouldScrollToTop(false);
      }

      let triggered = false;
      if (submitData) {
        const submitButton = activeStep.buttons.find(
          (b: any) => b.properties.submit
        );
        // Simulate button submit if available and valid to trigger button loader
        if (
          submitButton &&
          getNextStepKey({
            elementType: 'button',
            elementIDs: [submitButton.id]
          })
        ) {
          buttonOnClick(submitButton);
          triggered = true;
        }
      }
      if (!triggered) {
        const nextStep = getNextStepKey({
          elementType: 'field',
          elementIDs: [fieldID]
        });
        if (nextStep) {
          runElementActions({
            actions: [{ type: ACTION_NEXT }],
            element: { id: fieldID },
            elementType: 'field',
            submit: true
          });
        }
      }
    };

  const elementOnView =
    // be efficient and only create this function if we need it
    eventHasUserLogic('view')
      ? (elementId: any, isVisible: any) => {
          callbackRef.current.addCallback(
            runUserLogic('view', () => ({
              visibilityStatus: { elementId, isVisible }
            }))
          );
        }
      : undefined;

  if (internalState[_internalId]) {
    const callbacks = {
      buttonOnClick,
      runElementActions,
      tableOnClick,
      changeValue
    };
    if (assistantClientRef.current) {
      assistantClientRef.current.updateCallbacks(callbacks);
    } else {
      assistantClientRef.current = new AssistantClient(callbacks);
      internalState[_internalId].assistantClient = assistantClientRef.current;
    }
  }

  const form = {
    formInstanceId: _internalId,
    userProgress,
    curDepth,
    maxDepth,
    elementProps,
    customComponents,
    activeStep,
    steps,
    customClickSelectionState,
    runElementActions,
    buttonOnClick,
    tableOnClick,
    fieldOnChange,
    buttonLoaders,
    inlineErrors,
    setInlineErrors,
    changeValue,
    changeStep: (nextStepKey: string) =>
      changeFormStep(nextStepKey, activeStep.key, false),
    client,
    updateFieldValues,
    submitCustom: (values: Record<string, any>) => client?.submitCustom(values),
    elementOnView,
    onViewElements: viewElements,
    formSettings,
    focusRef,
    formRef,
    setCardElement,
    visiblePositions,
    calendly: integrations?.calendly?.metadata,
    featheryContext: getFormContext(_internalId),
    assistantClient: internalState[_internalId]?.assistantClient
  };

  // If form was completed in a previous session and edits are disabled,
  // consider the form finished
  const anyFinished =
    finished || (session?.form_completed && !formSettings.allowEdits);

  useEffect(() => {
    if (!anyFinished) return;
    const redirectForm = () => {
      if (trackHashes.current)
        navigate(
          featheryWindow().location.pathname + featheryWindow().location.search,
          { replace: true }
        );
      if (initState.redirectCallbacks[_internalId]) {
        hasRedirected.current = true;
        initState.redirectCallbacks[_internalId]();
      }
    };
    handleFormComplete().then(redirectForm);
  }, [anyFinished]);

  // Claim a single-use access link for this device, then restart the load so
  // the session fetch runs again against the submission the link opens.
  const confirmAccessLink = async (): Promise<LinkConfirmOutcome> => {
    const { linkToken } = initState;
    if (!linkToken || !client) return 'failed';

    // Fail safe: redeeming burns the one opening the link has, so a device that
    // cannot keep the device secret must not touch the server at all. The link
    // stays unredeemed and the user can open it somewhere that can store it.
    if (!canPersistLinkSecret()) return 'storage_blocked';

    let redemption: LinkRedemption | undefined;
    try {
      redemption = await client.redeemLink(linkToken);
    } catch (e) {
      // A rate limit or server error leaves the link unredeemed, so the user
      // can simply ask again
      return 'failed';
    }

    // A rejected link (expired, used on another device, revoked) answers 403,
    // which the client has already turned into the blocked form state
    if (initState.authenticationError) return 'blocked';
    // Anything else empty is a network failure or a response we can't open the
    // submission with, both of which are worth retrying
    if (!redemption?.fuser_key || !redemption.device_secret) return 'failed';

    adoptLinkRedemption(linkToken, redemption);
    remountAllForms();
    return 'adopted';
  };

  // Form authentication error (403)
  if (initState.authenticationError) {
    return (
      <FormOff
        reason={CLOSED}
        message={initState.authenticationError}
        showCTA={false}
      />
    );
  }
  // Form is turned off
  if (formOffReason.current === CLOSED)
    return (
      <FormOff
        showCTA={formSettings.showBrand}
        message={formOffMessage.current}
      />
    );
  else if (
    [
      COLLAB_COMPLETED,
      COLLAB_DIRECT_DISABLED,
      LINK_REQUIRED,
      NO_BUSINESS_EMAIL
    ].includes(formOffReason.current)
  )
    return <FormOff reason={formOffReason.current} showCTA={false} />;
  // Checked after the off states so a turned off form never offers to burn the
  // one opening a single-use link has
  else if (linkConfirmRequired)
    return <LinkContinue onContinue={confirmAccessLink} />;
  else if (anyFinished) {
    return formSettings.completionBehavior === 'show_completed_screen' ? (
      <FormOff reason={FILLED_OUT} showCTA={formSettings.showBrand} />
    ) : null;
  } else if (!activeStep) return stepLoader;

  return (
    <ReactPortal options={popupOptions}>
      <form
        {...formProps}
        autoComplete={formSettings.autocomplete}
        className={`feathery ${className || ''}`}
        ref={formRef}
        css={{
          ...globalCSS.getTarget('form'),
          ...stepCSS,
          ...style,
          ...assistantOffsetCSS,
          position: 'relative',
          display: 'flex',
          ...(popupOptions ? { borderRadius: '10px' } : {})
        }}
        dir={formSettings.rightToLeft ? 'rtl' : 'ltr'}
      >
        {stepLoader}
        {children}
        {showQuikFormViewer && (
          <QuikFormViewer
            html={quikHTMLPayload}
            setShow={setShowQuikFormViewer}
          />
        )}
        {dataMappingState.show && (
          <DataMappingModal
            hubs={dataMappingState.hubs}
            client={client}
            responsiveStyles={globalCSS}
            onClose={() => setDataMappingState((p) => ({ ...p, show: false }))}
          />
        )}
        {reviewViewerPayload && (
          <React.Suspense fallback={null}>
            <DocumentViewer
              payload={reviewViewerPayload.payload}
              action={reviewViewerPayload.action}
              setShow={(show: boolean) => {
                if (!show) {
                  clearLoaders();
                  // Let the opener know the user backed out — the logic-rule
                  // flow awaits a promise that must settle either way.
                  reviewViewerPayload.onClose?.();
                  setReviewViewerPayload(null);
                }
              }}
              onComplete={reviewViewerPayload.onComplete}
              onFinalize={reviewViewerPayload.onFinalize}
              // Persists PDFs whose fields the filler edited in the viewer.
              // The generic envelope file endpoint validates the extension
              // against the envelope type, so name the upload as a pdf.
              onSaveEnvelopeFile={(envelopeId: string, file: Blob) =>
                clientRef.current.saveEnvelopeFile(
                  envelopeId,
                  file,
                  'document.pdf'
                )
              }
            />
          </React.Suspense>
        )}
        {flinksFrame}
        <Grid step={activeStep} form={form} viewport={viewport} />
        {popupOptions && (
          <CloseIcon
            fill='white'
            css={{
              position: 'absolute',
              top: '17px',
              right: '17px',
              cursor: 'pointer'
            }}
            onClick={() => popupOptions.onHide && popupOptions.onHide()}
          />
        )}
        {initState.isTestEnv && (
          <DevNavBar
            allSteps={steps}
            curStep={activeStep}
            changeStep={(stepKey: string) => {
              if (trackHashes.current) navigate(getNewStepUrl(stepKey));
              else setStepKey(stepKey);
            }}
            formName={formName}
            draft={_draft}
            visible={!hideTestUI}
          />
        )}
        {global.firebase && (
          <div
            id='featheryRecaptcha'
            style={{ position: 'absolute', visibility: 'hidden' }}
          />
        )}
        <Watermark
          show={formSettings.showBrand}
          brandPosition={formSettings.brandPosition}
        />
        <ActionToast
          ref={setActionToastRef}
          data={actionToastData}
          bottom={bottomRightBase}
        />
        <FileUploadToast
          instanceId={_internalId}
          enabled={formSettings.showFileUploadProgress}
          bottom={bottomRightBase + stackAbove(actionToastHeight)}
        />

        {formSettings.assistantEnabled && (
          <AssistantChat
            instanceId={_internalId}
            baseUrl={`${new URL(API_URL).origin}/agent/assistant/`}
            getTargets={getAssistantTargets}
            bottom={
              bottomRightBase +
              stackAbove(actionToastHeight) +
              stackAbove(fileUploadToastHeight)
            }
            color={formSettings.assistantColor}
            workflowActions={formSettings.assistantWorkflowActions}
            stepSettings={formSettings.assistantStepSettings}
            activeStepId={activeStep?.id}
            onLayoutChange={handleAssistantLayoutChange}
          />
        )}
        {connectAccountModal && (
          <ConnectAccountModal
            show
            provider={connectAccountModal.provider}
            client={client}
            accountEmail={
              hasEmailIdentity(connectAccountModal.provider)
                ? (fieldValues[
                    connectionFieldKey(connectAccountModal.provider)
                  ] as string)
                : ''
            }
            onChangeAccount={async () => {
              // window.open must stay the first statement: the modal's
              // button handler invokes this synchronously from a real click,
              // and any await ahead of it would break the user-gesture chain
              // the popup relies on.
              const popup = featheryWindow().open(
                'about:blank',
                ACCOUNT_CONNECT_POPUP_NAME,
                getPopupFeatures()
              );
              // The modal's handler doesn't await/catch this beyond reading
              // the returned message, so a rejection here must not escape -
              // otherwise a blocked popup or a failed re-auth leaves
              // "Change account" looking inert with no explanation.
              try {
                const result = await runOAuthPopup(
                  client,
                  connectAccountModal.provider,
                  popup
                );
                updateFieldValues({
                  [connectionFieldKey(connectAccountModal.provider)]:
                    hasEmailIdentity(connectAccountModal.provider)
                      ? result.account_email ?? ''
                      : 'true'
                });
              } catch (error) {
                return error instanceof Error
                  ? error.message
                  : 'Unable to connect your account.';
              }
            }}
            onSaved={async (values) => {
              updateFieldValues(values);
              closeConnectAccountModal();
              await connectAccountModal.onFlowSuccess();
            }}
            onClose={() => {
              closeConnectAccountModal();
              clearButtonActionState();
              connectAccountModal.onAsyncEnd();
            }}
          />
        )}
      </form>
    </ReactPortal>
  );
}

// normal <Form /> (aka ReactForm) component is exported with just `props:
// Props`, so need this component to support exposing _internalId for use in
// renderAt without exposing InternalProps to SDK users
export function JSForm({
  formId,
  _internalId,
  _isAuthLoading = false,
  ...props
}: Props & InternalProps) {
  const [remount, setRemount] = useState(false);
  const confirmPopNavigationGuard = useCallback(
    () => confirmLeavingUnsavedWork(_internalId),
    [_internalId]
  );

  useEffect(() => {
    initState.remountCallbacks[_internalId] = () =>
      setRemount((remount) => !remount);
    return () => {
      delete initState.remountCallbacks[_internalId];
    };
  }, []);

  // Check client for NextJS support
  if (formId && runningInClient())
    return (
      <FeatheryCacheProvider>
        <RouterProvider confirmPopNavigation={confirmPopNavigationGuard}>
          <Form
            {...props}
            formId={formId}
            key={`${formId}_${remount}`}
            _internalId={_internalId}
            _isAuthLoading={_isAuthLoading}
          />
        </RouterProvider>
      </FeatheryCacheProvider>
    );
  else return null;
}

export default function ReactForm(props: Props) {
  let [internalId, setInternalId] = useState('');
  // Cannot use uuidv4 on server-side
  if (!internalId && runningInClient()) {
    internalId = uuidv4();
    setInternalId(internalId);
  }
  return <JSForm {...props} _internalId={internalId} />;
}
